// Direct imports
import { isProvenanceHistoryField } from './behaviorAssetFields';
import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Node } from "@babylonjs/core/node";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Material } from "@babylonjs/core/Materials/material";
import { MultiMaterial } from "@babylonjs/core/Materials/multiMaterial";
import type { BaseTexture } from "@babylonjs/core/Materials/Textures/baseTexture";
import type { Geometry } from "@babylonjs/core/Meshes/geometry";
import { SubMesh } from "@babylonjs/core/Meshes/subMesh";
import type { MorphTargetManager } from "@babylonjs/core/Morph/morphTargetManager";
import type { FloatArray, IndicesArray } from "@babylonjs/core/types";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { RandomGUID } from "@babylonjs/core/Misc/guid";
import type { Animation as BabylonAnimation } from "@babylonjs/core/Animations/animation.pure";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { GLTF2Export } from "@babylonjs/serializers";
import { SceneObjectData } from '../scenedata/sceneobjectdata';
import { TraceSceneInfoFile } from '../scenedata/infofiledata';
import { Frame } from '../behaviors/frame';
import { RENDER_BOUNDS_NAME } from '../core/Camera';
import { Video } from '../behaviors/video';
import { TraceBehavior } from '../behaviors/tracebehavior';
import { BehaviorData } from '../scenedata/behaviordata';
import { ResourceManager } from '../loaders/ResourceManager';
import { isSceneFormatRef } from '../loaders/plugins/SceneFormatRegistry';
import { Image } from '../behaviors/image';
import { Animation } from '../behaviors/animation';
import { MATERIAL_SETTLE_TIMEOUT_MS, Renderer } from '../behaviors/renderer';
import { Logger } from '../utils/Logger';
import { assertValidTransformData } from '../utils/validateTransformData';
import { extensionFromMimeType, isGlbBlob, isWebPageNotAsset } from '../utils/AssetTypes';
import { normalizePageDecks } from './normalizePageDecks';
import { isAnimationTargetInSet } from '../utils/AnimationUtils';
import { isLocalAssetPath, isSiteAbsolutePath } from '../utils/assetPaths';
import { TraceScene } from '../core/TraceScene';
import { SceneObject } from '../core/SceneObject';
import {
    convertImageAnchorPosition,
    convertImageAnchorRotation,
} from '../loaders/plugins/zipAnchorConversion';
import { ImageAnchor } from '../behaviors/imageanchor';
import { anchorHost } from '../loaders/ImageMarkerLoader';
import { type BakeStamp, bakedFromRig, bakeKindOf, bakeStamp, bevelBakeFilename, deformationBakeFilename, isBakedGeometrySrc, keepRememberedBake, NO_SRC_RIG, recordWrittenBake, rememberedBake, textBodyBakeFilename, unchangedBake } from '../loaders/restoreBakedSrc';
import { Text } from '../behaviors/text';
import { createTextBodyMaterial } from '../behaviors/text/textBody';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import {
    BUILTIN_PREFIX,
    collectUsedPrimitives,
    dropImpliedPrimitiveModels,
    generatePrimitiveGLB,
    generateGeometryGLB,
    getBuiltInSrc,
    getPrimitiveType,
    isBuiltInPrimitive,
    rewriteBuiltInPaths,
    PRIMITIVE_FILENAMES,
    PrimitiveType
} from './BuiltInPrimitives';
import { generateBevelGeometry } from '../behaviors/bevel';
import type { BevelGeometryParams, BevelHostPrimitive } from '../behaviors/bevel';
import { MorphStretch } from '../behaviors/morphstretch';
import { MeshBackground } from '../behaviors/meshbackground';
import { resolveTriplanarForBake, rigidFrameInverseToRef } from './triplanarPolicy';
import { ownedExportNodes, warnHelperMeshExcluded } from './exportFilters';
import type { TriplanarConfig } from './triplanarPolicy';
import { buildProjectedUV } from './uvProject';
import { toNonIndexed, computeTangents, weldByAttributes } from './meshGeometryOps';
import type { ZipProgressCallback } from './zip/zipWriter';
import { isAbsoluteHttpUrl } from './manifestValidator';
import { applySubtreeExclusion, withoutExcluded, type ExcludedSubtrees } from './exportExclusion';
import type { PropertySheet } from '../scenedata/behaviors/propertiesdata';
import { collectExportPropertySheets, propertySheetAssetDeclarations, repointPropertySheet } from './propertySheetAssets';
import { inlineGltfExternalAssets } from './gltfExternalAssets';

// 2024
// Author: Aaron Post

export interface TraceExportSceneOptions {
    /**
     * When false, external HTTP(S) URLs are preserved as-is instead of
     * being downloaded and bundled into the zip. Default: true.
     *
     * **Kept on purpose (WP-B2 flagged it for retirement; WP-F1 decided).**
     * It belongs to the LEGACY `.trace`/`.zip` writer, which has no folder to
     * lean on and no manifest classes: a `.trace` is self-contained or it is
     * broken, so `exportTraceFormat` still turns it on for that artifact. The
     * rule it was suspected of violating — "a library reference's bytes never
     * travel" — is enforced where it applies, in the folder writer, by the
     * manifest class (`byReference`), not by this flag; the folder path passes
     * `false` and never downloads a reference. Retiring it would mean
     * rewriting the legacy writers, which is WP-E's business, not F1's.
     */
    bundleExternalAssets?: boolean;
    /**
     * Bake live deformation (MorphStretch, MeshBackground, scale fallback,
     * triplanar projection) into the written geometry.
     *
     * Set by the WRITER, not by a host: see `SceneExporter.bakesFor`.
     */
    bake?: boolean;
    /**
     * Name the bake an authoring session opened, or stored in `folderUrl`,
     * instead of baking again, for each object nothing has reshaped since
     * (`unchangedBake`). Set by every folder-shaped writer (a save, `.glz`,
     * a copy to another folder); without `folderUrl` only opened bakes are
     * named, and an archive or copy fetches them like any folder file.
     */
    reuseUnchangedBakes?: { folderUrl?: string };
    /** Export only this SceneObject and its descendants. */
    subtreeRoot?: SceneObject;
    /**
     * Export several subtrees at once — the promotion path
     * (CONTENT_MODEL_SPEC §3.5). Wins over {@link subtreeRoot} when both are
     * given. With {@link groupRootName} the records are re-parented under one
     * synthesized structural root so the asset satisfies the single-root
     * contract (SCENE_ROOT_SPEC §4); the LIVE SCENE IS NOT TOUCHED — the group
     * exists only in the records this preparation produces.
     */
    subtreeRoots?: readonly SceneObject[];
    /** Name of the synthesized group root — see {@link subtreeRoots}. */
    groupRootName?: string;
    /**
     * Subtrees to leave out of this export — see
     * {@link SubtreeExclusionOptions.exclude}. Applied to the captured records
     * before any asset, bake or writer pass sees them, so an excluded subtree
     * is invisible to the whole pipeline (CONTENT_MODEL_SPEC §2.9).
     */
    exclude?: ExcludedSubtrees;
    /**
     * Whether the scene is attached to an image-marker anchor — decides the
     * `.zip` frame conversion. From the CMS record (`isSceneAnchored`);
     * undefined → runtime-behavior fallback.
     * Contract: docs-internal/ZIP_FRAME_CONTRACT.md.
     */
    imageMarker?: boolean;
    /** Fires after each archive entry is written during ZIP assembly. */
    onProgress?: ZipProgressCallback;
}

/**
 * Map an asset REF as it appears in a prepared record to its KEY in the asset
 * map {@link TraceExport.prepareSceneContent} returns. The two differ by a
 * `./assets/` prefix and nothing else.
 *
 * Every key in that map is `ResourceManager.removeAssetsPrefix(ref)` of the
 * ref that bundled it (or a bare generated filename, which the same call
 * leaves alone), so this is exact rather than best-effort. Keys are NOT
 * uniformly bare filenames: a nested scene's subassets are re-homed to
 * `assets/<sceneFolder>/<name>` by `ResourceManager.mergeSceneAssets` and keep
 * that subpath here, which is precisely what stops two scenes' same-named
 * `panel.glb` from colliding.
 *
 * Which is also why packagers must NOT fall back to the ref's basename when
 * this misses: under scene-scoped keys the basename is the ambiguous part, so
 * a basename probe resolves one nested asset to another's bytes. A miss means
 * the ref was never bundled by this preparation — handle it as such.
 */
export function preparedAssetKey(ref: string): string {
    return ResourceManager.removeAssetsPrefix(ref);
}

/**
 * Behavior fields that are links or authored text, never assets. Bundling a
 * link would copy whatever it points at into the archive and rewrite it to
 * that copy, which cuts the link it exists to keep. A Text's `text` is what
 * the label says: "/ 2025" is content, not a path to download.
 */
const LINK_ONLY_FIELDS: Readonly<Record<string, ReadonlySet<string>>> = {
    // Where a placed asset came from in the library (a `/assets/{id}/file`
    // URL): the join back to the library entry. The placed nodes already
    // carry the files they render. `remixOf` names the scene this one was
    // derived from — a link too, never something this scene renders.
    Provenance: new Set(['placedFrom', 'remixOf']),
    Text: new Set(['text']),
};

function isLinkOnlyField(record: Record<string, unknown>, key: string): boolean {
    const type = record['@type'];
    return typeof type === 'string' && (LINK_ONLY_FIELDS[type]?.has(key) ?? false);
}

export class TraceExport {
    private resourceManager: ResourceManager;

    /**
     * Record → live SceneObject pairing built by {@link exportHierarchy}.
     * Later export passes consult it for runtime state the serialized data
     * cannot carry (live morph influences). WeakMap: entries die with the
     * export data.
     */
    private _liveByRecord = new WeakMap<SceneObjectData, SceneObject>();
    /** Where unchanged bakes come from, when they are reused. */
    private _reuseBakesFrom: { resources: ResourceManager; folderUrl?: string } | undefined;
    private _propertySheets = new Map<string, PropertySheet>();
    private _changedPropertySheets = new Set<string>();

    /**
     * The live SceneObject a prepared record was captured from, when this
     * preparation knows it (see {@link _liveByRecord}).
     *
     * Read by writer-side passes that have to ask the LIVE scene something the
     * records cannot answer — the legacy → 2.1 reference migration
     * (`dropImportEraReferenceCopies`) needs the content under a reference
     * node, which the export deliberately does not serialize.
     */
    public liveObjectFor(record: SceneObjectData): SceneObject | undefined {
        return this._liveByRecord.get(record);
    }

    constructor() {
        this.resourceManager = new ResourceManager();
    }

    /**
     * Run the full export preparation — capture, anchor conversion, the
     * Bevel and deformed-geometry bakes (morphs, corner radii, scale folds,
     * UV re-projection), primitive generation, URL handling — and hand back
     * the prepared records plus every produced asset, WITHOUT zipping.
     *
     * The `.tracex` packager consumes exactly what the `.trace` zip would
     * carry: one preparation, two packagers, so every export format ships
     * the same baked scene. (The bake passes used to run only inside the
     * `.trace` writer, which silently exempted `.tracex`/`.glxf` from the
     * baked-export contract.)
     */
    /**
     * Note for consumers calling `exportScene` directly (the sandbox MCP
     * harness does): the assets-always-bake decision is made by the WRITER in
     * `SceneExporter`, so a direct call gets whatever `options.bake` says and
     * does not inherit that invariant.
     */
    public async prepareSceneContent(
        scene: Scene,
        sceneName: string,
        traceScene?: TraceScene,
        options?: TraceExportSceneOptions,
    ): Promise<{
        content: SceneObjectData[];
        assets: Map<string, Blob>;
        propertySheets: Map<string, PropertySheet>;
        /** Asset keys whose sheet bytes preparation rewrote. */
        changedPropertySheets: ReadonlySet<string>;
    }> {
        const content = await this.prepareExport(scene, sceneName, undefined, traceScene, options);
        return {
            content,
            assets: new Map(this.resourceManager.getAssetEntries()),
            propertySheets: this._propertySheets,
            changedPropertySheets: this._changedPropertySheets,
        };
    }

    /**
     * Find the actual asset blob for a declared src
     * Returns [blob, actualSrc] or [undefined, undefined] if not found
     */
    private resolveAsset(
        declaredSrc: string | undefined,
        sourceResources: ResourceManager | undefined
    ): [Blob | undefined, string | undefined] {
        if (!sourceResources || !declaredSrc) {
            return [undefined, undefined];
        }

        // Skip absolute http(s) URLs and site-absolute paths (`/generated/…`)
        // — they are preserved as external references and handled later by the
        // bundling phase (which decides pack vs keep, and derives a flat
        // assets/ filename rather than nesting the server path).
        //
        // `isAbsoluteHttpUrl` rather than a `startsWith('http')` string test:
        // one predicate, shared with the manifest classifier, that refuses to
        // call a leading-slash path absolute (the Unix `file:` landmine,
        // tb#848).
        if (isAbsoluteHttpUrl(declaredSrc) || isSiteAbsolutePath(declaredSrc)) {
            return [undefined, undefined];
        }

        // Skip builtin: paths — they are handled separately by the primitive
        // generation step (collectUsedPrimitives + generatePrimitiveGLB + rewriteBuiltInPaths).
        if (declaredSrc.startsWith(BUILTIN_PREFIX)) {
            return [undefined, undefined];
        }

        // Direct lookup for local paths
        const blob = sourceResources.getAsset(declaredSrc);
        if (blob) {
            Logger.log(`[TraceExport] Found blob using declared src: ${declaredSrc}`);
            return [blob, declaredSrc];
        }

        // A folder file loaded over the network holds no blob here — it lives
        // in the ResourceManager as a URL MAPPING (`registerAssetUrl`), because
        // the folder already has the bytes and re-downloading them to re-upload
        // them is the thing the folder model exists to stop (spec §2.2). The
        // reference is valid; there is simply nothing local to carry. The
        // packagers decide what to do with it — a folder save names it and
        // sends no bytes, an archive fetches it — so say nothing here, and in
        // particular do not report a miss.
        if (sourceResources.resolveAssetUrl(declaredSrc)) {
            Logger.debug(`[TraceExport] Folder file already in its folder, no local blob: ${declaredSrc}`);
            return [undefined, undefined];
        }

        Logger.error(`[TraceExport] Could not find blob for declared src: ${declaredSrc}`);
        return [undefined, undefined];
    }

    /**
     * Helper to process asset for a node: resolves blob, adds to RM, updates src
     */
    private processAssetForNode(
        serializedNode: SceneObjectData,
        declaredSrc: string | undefined,
        sourceResources: ResourceManager | undefined,
        preloadedBlob?: Blob
    ): void {
        // If we already have a blob (e.g. from mesh), use it directly
        if (preloadedBlob && declaredSrc) {
             const assetFileName = ResourceManager.removeAssetsPrefix(declaredSrc);
             this.resourceManager.addAsset(assetFileName, preloadedBlob);
             serializedNode.src = `./assets/${assetFileName}`;
             Logger.log(`[TraceExport] Added preloaded blob to assets: ${assetFileName}`);
             return;
        }

        const [blob, actualSrc] = this.resolveAsset(declaredSrc, sourceResources);

        if (blob && actualSrc) {
            const assetFileName = ResourceManager.removeAssetsPrefix(actualSrc);
            this.resourceManager.addAsset(assetFileName, blob);
            serializedNode.src = `./assets/${assetFileName}`;
            Logger.log(`[TraceExport] Added asset blob to assets: ${assetFileName}`);
        } else {
            // Keep declared src even if blob not found (for external URLs)
            serializedNode.src = declaredSrc;
        }
    }

    /**
     * Extract and copy assets referenced in behavior data
     * Scans behavior objects for asset paths (fields ending with known asset patterns)
     * and copies them to the export's ResourceManager
     */
    private extractBehaviorAssets(behaviors: readonly unknown[], sourceResources: ResourceManager | undefined): void {
        if (!sourceResources || !behaviors) return;

        const processValue = (value: unknown): void => {
            if (isLocalAssetPath(value)) {
                const [blob, actualSrc] = this.resolveAsset(value, sourceResources);
                if (blob && actualSrc) {
                    const assetFileName = ResourceManager.removeAssetsPrefix(actualSrc);
                    this.resourceManager.addAsset(assetFileName, blob);
                    Logger.log(`[TraceExport] Extracted behavior asset: ${assetFileName}`);
                }
            } else if (Array.isArray(value)) {
                value.forEach(item => processValue(item));
            } else if (value && typeof value === 'object') {
                const record = value as Record<string, unknown>;
                for (const [key, entry] of Object.entries(record)) {
                    if (!isProvenanceHistoryField(record, key)) processValue(entry);
                }
            }
        };

        behaviors.forEach(processValue);
    }

    // Function to export the hierarchy using traceScene sceneObjects
    exportHierarchy(
        scene: Scene,
        traceScene?: TraceScene,
        options?: {
            includeRoot?: boolean;
            subtreeRoot?: SceneObject;
            subtreeRoots?: readonly SceneObject[];
            groupRootName?: string;
            exclude?: ExcludedSubtrees;
        },
    ): SceneObjectData[] {
        if (traceScene) {
            // Build paired SceneObjectData[] + SceneObject[] so processAssets can
            // read live behaviors without a name-keyed lookup (names aren't unique).
            let exportData: SceneObjectData[];
            let liveObjects: (SceneObject | null)[];

            if (options?.subtreeRoots?.length) {
                const built = traceScene._buildExportDataForSubtrees(options.subtreeRoots, options.groupRootName);
                exportData = built.data;
                liveObjects = built.sceneObjects;
            } else if (options?.subtreeRoot) {
                const built = traceScene._buildExportDataForSubtree(options.subtreeRoot);
                exportData = built.data;
                liveObjects = built.sceneObjects;
            } else if (options?.includeRoot !== false) {
                // The document: file-local paths, exactly one root record —
                // the document root, a real object of the scene carrying its
                // own behaviors (SCENE_ROOT_SPEC D8). A session without one
                // gets a synthesized root here, loudly.
                const built = traceScene._buildDocumentExportData();
                exportData = built.data;
                liveObjects = built.sceneObjects;
            } else {
                // `.zip` — the legacy Unity delivery format, frozen. It is a
                // FLAT list with no root record: the published archive IS the
                // scene, and a scene owns no root-entity behaviors by design
                // (SCENE_ROOT_SPEC §3). So the document root is the node this
                // format has always omitted — its children become top-level and
                // its behaviors do not travel.
                const built = traceScene._buildExportData();
                exportData = built.data;
                liveObjects = built.sceneObjects;
                TraceScene.stripSceneRootPrefix(exportData);
                // The node this format has always omitted: the root the SESSION
                // was opened with (a `.trace`'s structural root, or one an
                // editor adopted). A session that never established one has no
                // such node, and every top-level object is content — dropping
                // one there would delete it.
                const sessionRoot = traceScene.getSessionDocumentRoot();
                const documentRootIndex = sessionRoot ? liveObjects.indexOf(sessionRoot) : -1;
                if (documentRootIndex >= 0) {
                    TraceScene.stripSceneRootPrefix(exportData, exportData[documentRootIndex].name);
                    exportData.splice(documentRootIndex, 1);
                    liveObjects.splice(documentRootIndex, 1);
                }
            }

            // Editor-only content and anything else this save is told not to
            // carry (CONTENT_MODEL_SPEC §2.9). Cut HERE — before the pairing,
            // before `processAssets`, before any writer — so the excluded
            // records simply never existed: no behaviors, no `files[]` entries
            // of their own, no interactivity. A file a kept node also names is
            // collected from that node and stays.
            if (options?.exclude) {
                const kept = applySubtreeExclusion(exportData, liveObjects, options.exclude);
                exportData = kept.data;
                liveObjects = kept.liveObjects;
            }

            // Pair each record with its live SceneObject for later passes
            // that need runtime state the serialized data cannot carry
            // (bakeDeformedNodes reads live morph influences). Keyed by
            // record identity — names are not unique.
            for (let i = 0; i < exportData.length; i++) {
                const liveObject = liveObjects[i];
                if (liveObject) this._liveByRecord.set(exportData[i], liveObject);
            }

            for (const record of exportData) retireStaleBakedFrom(record);
            this.processAssets(exportData, liveObjects, traceScene);
            return exportData;
        } else {
            // Fallback to old method if traceScene not provided
            const hierarchy: SceneObjectData[] = [];
            const nodesToProcess: TransformNode[] = [];

            scene.transformNodes.forEach(node => {
                if (!node.parent && node.name != RENDER_BOUNDS_NAME) {
                    Logger.log(`Serializing root node: ${node.name}`);
                    nodesToProcess.push(node);
                }
            });

            this.processLayer(nodesToProcess, hierarchy);
            return hierarchy;
        }
    }

    /**
     * Process asset references for exported scene data.
     * Copies asset blobs to the export ResourceManager and updates src paths.
     *
     * `exportData` and `liveObjects` are parallel arrays — each data entry is
     * paired with the SceneObject it was built from (or null for the synthetic
     * scene root entry, which has no live owner of its own).
     */
    private processAssets(
        exportData: SceneObjectData[],
        liveObjects: (SceneObject | null)[],
        traceScene: TraceScene
    ): void {
        const sourceResources = traceScene.resources;

        // Mark descendants of external scene-format references so their assets
        // are not extracted at this level — they live inside the parent's
        // external file (a nested .trace/.tracex). Asset srcs (models, images,
        // videos) never materialize child SceneObjects, so children under them
        // are authored and their assets must be extracted. Tracked by
        // SceneObject identity (via the paired liveObjects array) since names
        // are only sibling-unique.
        // Must classify the same way `collectExternalUrls` does — an external
        // ancestor missed here has its children walked for extraction at this
        // level, pulling assets that live inside the parent's own file.
        const isExternal = (src: string | undefined) =>
            isAbsoluteHttpUrl(src) || isSiteAbsolutePath(src);

        const externalAncestors = new Set<SceneObject>();
        for (let i = 0; i < exportData.length; i++) {
            const so = liveObjects[i];
            const src = exportData[i].src;
            if (so && isExternal(src) && isSceneFormatRef(src)) {
                externalAncestors.add(so);
            }
        }
        // Single pass relies on topological order — parents always precede their
        // children in the sorted export array, so by the time we visit a child
        // its external ancestor (if any) is already in the set.
        const skipChildren = new Set<number>();
        for (let i = 0; i < exportData.length; i++) {
            const so = liveObjects[i];
            if (!so) continue;
            const parent = so.getParent();
            if (parent && externalAncestors.has(parent)) {
                skipChildren.add(i);
                externalAncestors.add(so);
            }
        }

        for (let i = 0; i < exportData.length; i++) {
            const data = exportData[i];
            const sceneObject = liveObjects[i];

            if (skipChildren.has(i)) continue;

            // Pack any referenced asset (3D models, etc.)
            if (data.src) {
                this.processAssetForNode(data, data.src, sourceResources);
            }

            // Get src from behaviors (for videos, images) — needs the live object.
            if (sceneObject) {
                const videoBehavior = sceneObject.getBehavior(Video);
                if (videoBehavior) {
                    let declaredSrc = videoBehavior.sceneObject.src;
                    const videoComponent = data.behaviors?.find(component =>
                        component["@type"] === "VideoPlayer" || component["@type"] === "Video"
                    );
                    if (videoComponent) {
                        const override = (videoComponent as BehaviorData & { src?: string }).src;
                        if (override) {
                            declaredSrc = override;
                        }
                    }
                    this.processAssetForNode(data, declaredSrc, sourceResources);
                } else {
                    const imageBehavior = sceneObject.getBehavior(Image);
                    if (imageBehavior) {
                        const declaredSrc = imageBehavior.sceneObject.src;
                        this.processAssetForNode(data, declaredSrc, sourceResources);
                    }
                }
            }

            // Extract assets from all behavior data (e.g., ParticleSystem.texture)
            if (data.behaviors) {
                this.extractBehaviorAssets(data.behaviors, sourceResources);
            }
        }
    }

    processLayer(nodes: TransformNode[], hierarchy: SceneObjectData[]): void {
        const nextLayer: TransformNode[] = [];

        nodes.forEach(node => {
            const metadataBehaviors = node.metadata as BehaviorData[] | null;
            Logger.log(`[TraceExport] Serializing node: ${node.name}, behaviors: ${node.behaviors?.length || 0}, metadata components: ${metadataBehaviors?.length || 0}`);

            // Get parent name, but avoid self-referential cycles
            const parentName = node.parent?.name;
            const safeParentName = (parentName && parentName !== node.name) ? parentName : undefined;

            const serializedNode: SceneObjectData = {
                name: node.name,
                src: undefined,
                active: node.isEnabled(false),
                transform: {
                    translation: node.position.asArray(),
                    rotation: node.rotationQuaternion?.asArray(),
                    scale: node.scaling.asArray(),
                    parent: safeParentName
                },
                behaviors: []
            };

            let recurse = true;

            // Copy components from metadata, ensuring animation properties are preserved
            serializedNode.behaviors = metadataBehaviors ? [...metadataBehaviors] : [];
            if (metadataBehaviors && metadataBehaviors.length > 0) {
                Logger.log(`[TraceExport] Copied ${metadataBehaviors.length} components from metadata for ${node.name}`);
            }

            // Note: 3D models (GLB, GLTF, etc.) are now loaded directly without behaviors.
            // In the fallback path (without TraceScene), we can't easily get the src.
            // The primary export path using serializeSceneObjects handles this correctly.

            for (const behavior of node.behaviors) {
                if (behavior instanceof Video) {
                    serializedNode.src = behavior.sceneObject.src;

                    const videoComponent = serializedNode.behaviors.find(component =>
                        component["@type"] === "VideoPlayer" || component["@type"] === "Video"
                    );
                    const override = videoComponent ? (videoComponent as BehaviorData & { src?: string }).src : undefined;
                    if(override) {
                        serializedNode.src = override;
                    }
                    recurse = false;
                }
                else if (behavior instanceof Image) {
                    serializedNode.src = behavior.sceneObject.src;
                    recurse = false;
                }
                else if (behavior instanceof Frame) {
                    recurse = false;
                }
                else if (behavior instanceof Animation) {
                    // Animation behaviors are already handled through metadata
                    // The data contains EnterExitAnimation and WhileActiveAnimation properties
                    Logger.log(`Serializing animation behavior for ${node.name}`);
                }
                else if (behavior instanceof Renderer) {
                    // Renderer behaviors are already handled through metadata
                    // The data contains materials array with replacement information
                    Logger.log(`Serializing renderer behavior for ${node.name}`);
                }
            }

            Logger.log(`[TraceExport] Final serialized node ${node.name}:`, {
                src: serializedNode.src,
                componentsCount: serializedNode.behaviors.length,
                hasParent: !!serializedNode.transform?.parent
            });
            
            hierarchy.push(serializedNode);

            if (recurse) {
                const children = node.getChildTransformNodes(true);
                Logger.log(`[TraceExport] Node ${node.name} has ${children.length} children, will recurse`);
                children.forEach(child => {
                    if (child != null) {
                        nextLayer.push(child);
                    }
                });
            } else {
                Logger.log(`[TraceExport] Skipping recursion for node: ${node.name}`);
                Logger.log(`Skipping recursion for node: ${node.name}`);
            }
        });

        if (nextLayer.length > 0) {
            Logger.log(`[TraceExport] Processing next layer with ${nextLayer.length} nodes`);
            this.processLayer(nextLayer, hierarchy);
        }
    }

    serializeBehavior(behavior: TraceBehavior): BehaviorData {
        return behavior.serialize();
    }

    /**
     * Collect all external URLs from the scene hierarchy
     */
    private collectExternalUrls(hierarchy: readonly unknown[]): Set<string> {
        const urls = new Set<string>();

        const isExternalUrl = (url: unknown): url is string => {
            if (!url || typeof url !== 'string') return false;
            // Site-absolute paths (e.g. `/generated/<hash>.jpg` from image
            // generation) are server-served assets: without bundling, the
            // package references a file it never contains and media vanishes
            // outside the authoring server.
            if (isSiteAbsolutePath(url)) return true;
            return isAbsoluteHttpUrl(url);
        };

        for (const record of walkExportRecords(hierarchy)) {
            // Check for src field
            if (isExternalUrl(record.src)) {
                urls.add(record.src);
            }

            for (const key of Object.keys(record)) {
                if (isLinkOnlyField(record, key) || isProvenanceHistoryField(record, key)) continue;
                if (isExternalUrl(record[key])) {
                    urls.add(record[key]);
                }
            }
        }
        return urls;
    }

    /**
     * Download a URL and return it as a blob, or `null` when there is nothing
     * to bundle.
     *
     * `null` is the one answer for every way a URL can fail to be an asset:
     * the server says it is not there (a 404), the request never completed
     * (offline, CORS), or it answered with a web page ({@link isWebPageNotAsset}).
     * Each is logged with the URL, and in each the reference is left exactly as
     * authored and the export goes on.
     *
     * A page in place of the file is a 404 by another name — a dev server's SPA
     * fallback says "not here" with a 200 — so it is not the one case that
     * fails the save. The scan behind this reads any string that looks like a
     * URL as an asset, so what turns out not to be one may be a link to a live
     * page or a label that starts with a slash, and a scene with one stale
     * `src` must still save.
     */
    private async downloadAsset(url: string): Promise<Blob | null> {
        try {
            Logger.log(`[TraceExport] Downloading asset: ${url}`);
            const response = await fetch(url);
            if (!response.ok) {
                Logger.warn(`[TraceExport] Failed to download ${url}: ${response.status}`);
                return null;
            }
            const body = await response.arrayBuffer();
            const contentType = response.headers.get('content-type');
            // Before it becomes a named file in the archive: a 200 that is
            // really a dev server's SPA fallback is not the asset.
            if (isWebPageNotAsset(url, contentType, body)) {
                Logger.warn(`[TraceExport] Failed to download ${url}: it answered with a web page, not an asset`);
                return null;
            }
            const blob = new Blob([body], { type: contentType ?? '' });
            Logger.log(`[TraceExport] Downloaded ${url} (${blob.size} bytes)`);
            return blob;
        } catch (error) {
            Logger.error(`[TraceExport] Error downloading ${url}:`, error);
            return null;
        }
    }

    /**
     * Generate a filename from a URL. A URL with no extension of its own
     * (`/assets/<id>/file`) gets `fallbackExtension`, which the caller reads
     * from the downloaded blob.
     */
    private getFilenameFromUrl(url: string, fallbackExtension = 'bin'): string {
        const randomName = (): string => {
            const hash = Math.random().toString(36).substring(2, 15);
            return `asset_${hash}.${fallbackExtension}`;
        };
        try {
            // Base makes site-absolute paths parse instead of throwing into
            // the random-name fallback.
            const urlObj = new URL(url, 'http://local');
            const pathname = urlObj.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
            if (!filename || !filename.includes('.')) return randomName();
            return filename;
        } catch {
            return randomName();
        }
    }

    /**
     * Rewrite external URLs to local asset paths in the hierarchy
     */
    private rewriteUrls(hierarchy: SceneObjectData[], urlMap: Map<string, string>): void {
        for (const record of walkExportRecords(hierarchy)) {
            // Every bundled string field, `src` included: the
            // bytes are in the archive under the mapped name, so a ref left
            // pointing at the original URL resolves to nothing offline.
            //
            // `walkExportRecords` yields nested objects and array elements too,
            // so a ref inside a behavior sub-object (a legacy Material's
            // `slots[].replacementGLB`, a ParticleSystem `texture`) is reached
            // here as its own record, with no per-shape special case ahead of
            // the loop to consume a key before it is seen.
            for (const key of Object.keys(record)) {
                if (isLinkOnlyField(record, key) || isProvenanceHistoryField(record, key)) continue;
                const value = record[key];
                if (typeof value === 'string' && urlMap.has(value)) {
                    Logger.log(`[TraceExport] Rewriting ${key}: ${value} -> ${urlMap.get(value)}`);
                    record[key] = urlMap.get(value);
                }
            }
        }
    }

    /**
     * Build hierarchy, collect assets, generate primitives,
     * and populate the ResourceManager with all data.
     *
     * @param options - See {@link TraceExportSceneOptions}.
     */
    private async prepareExport(scene: Scene, sceneName: string, infoJson: TraceSceneInfoFile | undefined, traceScene?: TraceScene, options?: TraceExportSceneOptions): Promise<SceneObjectData[]> {
        const bundleExternalAssets = options?.bundleExternalAssets ?? true;
        this._reuseBakesFrom = options?.reuseUnchangedBakes && traceScene
            ? { resources: traceScene.resources, folderUrl: options.reuseUnchangedBakes.folderUrl }
            : undefined;
        const isZipFormat = !!infoJson;
        const hierarchy = this.exportHierarchy(scene, traceScene, {
            includeRoot: !isZipFormat,
            subtreeRoot: options?.subtreeRoot,
            subtreeRoots: options?.subtreeRoots,
            groupRootName: options?.groupRootName,
            exclude: options?.exclude,
        });
        for (const node of hierarchy) assertValidTransformData(node.transform, node.name);

        // A deck is written from its first page, not from the page the editor
        // was showing — see normalizePageDecks. It gets `_liveByRecord` so it
        // can ask each deck for its own page set instead of matching on names.
        normalizePageDecks(hierarchy, this._liveByRecord);
        this._propertySheets = collectExportPropertySheets(hierarchy, record => this._liveByRecord.get(record));

        // Legacy `.zip` image-marker scenes need their top-level
        // transforms re-encoded into Unity AR Foundation's anchor-local
        // frame on the way out — the editor stores them in OpenXR-style
        // (+Z normal, +Y top), but legacy Unity AR runtime expects
        // (+Y normal, +Z top, LH). Anywhere `.zip`s pass through (the
        // .zip format already uses glTF-RH for non-anchor content).
        // Newer formats (`.tracex`, a folder, a standalone `.gltf`) skip
        // entirely — they're
        // OpenXR-native.
        //
        // Which anchor the scene has is declared by the caller from the CMS
        // record (`options.imageMarker`). The runtime scene-root `ImageAnchor`
        // behavior is only a fallback: it reflects the anchor, it doesn't
        // define it, and it attaches asynchronously — exports can race it.
        // Contract: docs-internal/ZIP_FRAME_CONTRACT.md; races reproduced in
        // test/vitest/export/zip-marker-frame-races.test.ts.
        const declaredImageMarker = options?.imageMarker;
        const isImageMarker = declaredImageMarker
            ?? !!anchorHost(traceScene)?.getBehavior(ImageAnchor);
        const isImageMarkerZip = isZipFormat && isImageMarker;
        if (isImageMarkerZip) {
            applyEditorToUnityZipConversion(hierarchy);
        } else if (isZipFormat && declaredImageMarker === undefined) {
            // Nothing declared and no behavior found — correct for an
            // Anywhere scene, silently corrupting for an anchored one.
            // Loud so a racy caller is visible; declared exports stay quiet.
            Logger.warn(
                '[TraceExport] .zip frame decided by runtime fallback: no ImageAnchor found, '
                + 'exporting WITHOUT the anchor-frame conversion. If this scene is image-anchored '
                + 'in the CMS, pass `imageMarker` (from `isSceneAnchored(scene)`).',
            );
        }

        // Bake Bevel-modified primitives to geometry assets FIRST — baked
        // nodes leave the builtin flow (their src now points at the baked
        // GLB), so clients with no Bevel implementation render the rounded
        // result. Must run before collectUsedPrimitives.
        await this.bakeBevelNodes(hierarchy, scene);
        await this.bakeTextBodies(hierarchy, scene);

        // Bake morph-deformed geometry the same way, when the writer asks
        // (`SceneExporter.bakesFor`): MorphStretch sizing and MeshBackground
        // corner shaping are live morph state — clients that don't run the
        // behaviors (raw GLB consumers, Unity/glTFast, viewers degrading to
        // GenericBehavior) would otherwise render the rest shape: wrong size,
        // sharp corners.
        if (options?.bake) {
            await this.bakeDeformedNodes(hierarchy, scene);
        }

        // Generate and bundle built-in primitives
        Logger.log('[TraceExport] Collecting built-in primitives...');
        const usedPrimitives = collectUsedPrimitives(hierarchy);
        Logger.log(`[TraceExport] Found ${usedPrimitives.size} built-in primitives to generate`);

        if (usedPrimitives.size > 0) {
            const primitivePromises = Array.from(usedPrimitives).map(async (primitiveType: PrimitiveType) => {
                Logger.log(`[TraceExport] Generating GLB for primitive: ${primitiveType}`);
                const blob = await generatePrimitiveGLB(primitiveType, scene);
                const filename = PRIMITIVE_FILENAMES[primitiveType];
                this.resourceManager.addAsset(filename, blob);
                Logger.log(`[TraceExport] Added primitive GLB: ${filename} (${blob.size} bytes)`);
            });

            await Promise.all(primitivePromises);
            Logger.log(`[TraceExport] Generated ${usedPrimitives.size} primitive GLBs`);

            // Rewrite builtin: paths to local asset paths
            Logger.log('[TraceExport] Rewriting builtin paths to local paths...');
            rewriteBuiltInPaths(hierarchy);
            dropImpliedPrimitiveModels(hierarchy);
        }

        // Collect all external URLs
        const sourceResources = traceScene?.resources;
        const sheetAssets = [...this._propertySheets.values()].flatMap(sheet =>
            propertySheetAssetDeclarations(sheet).map(declaration => declaration.value));
        this.extractBehaviorAssets(sheetAssets, sourceResources);
        Logger.log('[TraceExport] Collecting external URLs...');
        const collected = this.collectExternalUrls([hierarchy, sheetAssets]);

        // `bundleExternalAssets=false` (the legacy `.zip` path) keeps remote
        // references as-is so the archive goes on pointing at their host.
        // A site-absolute path names no host, so "preserving" one ships a
        // reference only the authoring origin can resolve — the asset is
        // simply missing everywhere else. Those bundle either way.
        const externalUrls = bundleExternalAssets
            ? collected
            : new Set([...collected].filter(url => isSiteAbsolutePath(url)));
        Logger.log(
            bundleExternalAssets
                ? `[TraceExport] Found ${externalUrls.size} external URLs to download`
                : `[TraceExport] Preserving remote URLs (bundleExternalAssets=false); `
                    + `bundling ${externalUrls.size} site-absolute asset(s)`,
        );

        // Download all external assets (use cached blob from source ResourceManager when available)
        const urlMap = new Map<string, string>();
        if (externalUrls.size > 0) {
            const downloadPromises = Array.from(externalUrls).map(async (url) => {
                // Check source ResourceManager for cached blob (e.g. catalog imports)
                const loadedSheet = this._propertySheets.get(url);
                const cachedBlob = sourceResources?.getAsset(url)
                    ?? (loadedSheet ? new Blob([JSON.stringify(loadedSheet)], { type: 'application/json' }) : undefined);
                const blob = cachedBlob ?? await this.downloadAsset(url);
                if (blob) {
                    const typed = extensionFromMimeType(blob.type);
                    // A blob served with no usable type is still a model when it opens with the GLB magic.
                    const extension = typed ?? (await isGlbBlob(blob) ? 'glb' : undefined);
                    // Two URLs can end in the same basename (#631): different
                    // bytes take the next free name instead of the first's.
                    const filename = await this.resourceManager.addAssetWithDedup(this.getFilenameFromUrl(url, extension), blob);
                    urlMap.set(url, `./assets/${filename}`);
                    Logger.log(`[TraceExport] Mapped ${url} -> ./assets/${filename}${cachedBlob ? ' (from cache)' : ''}`);
                }
            });

            await Promise.all(downloadPromises);
            Logger.log(`[TraceExport] Downloaded ${urlMap.size} assets successfully`);

            // Rewrite URLs in the hierarchy
            Logger.log('[TraceExport] Rewriting URLs to local paths...');
            this.rewriteUrls(hierarchy, urlMap);
        }
        if (sourceResources) {
            const originalPaths = new Map([...urlMap].map(([original, prepared]) => [preparedAssetKey(prepared), original]));
            for (const [key, blob] of this.resourceManager.getAssetEntries()) {
                const path = originalPaths.get(preparedAssetKey(key)) ?? `./assets/${preparedAssetKey(key)}`;
                try {
                    this.resourceManager.addAsset(key, await inlineGltfExternalAssets(path, blob, sourceResources));
                } catch (error) {
                    // Legacy downloads preserve partial content; the strict folder resolver validates separately.
                    Logger.warn(`[TraceExport] ${error instanceof Error ? error.message : String(error)}; preserving the original model`);
                }
            }
        }
        this.writePropertySheets(urlMap);

        const now = new Date();
        const formattedDate = now.toISOString();

        // Include scripts map and extract script asset blobs
        let scriptsMap: Record<string, string> | undefined;
        if (traceScene?.scriptsMap) {
            scriptsMap = traceScene.scriptsMap;
            // Ensure script .ts files are included in exported assets
            for (const assetPath of Object.values(scriptsMap)) {
                const blob = traceScene.resources.getAsset(assetPath);
                if (blob) {
                    const assetFileName = ResourceManager.removeAssetsPrefix(assetPath);
                    this.resourceManager.addAsset(assetFileName, blob);
                }
            }
        }

        const exportedData = {
            sceneName: sceneName,
            version: "2.0.0",
            content: hierarchy.map(withoutNodeUid),
            ...(scriptsMap && { scripts: scriptsMap }),
            generator: `This file was generated by the Trace Sandbox Exporter on ${formattedDate}.`
        };

        if(infoJson) {
            this.resourceManager.setJson(exportedData, infoJson);
        }
        else {
            this.resourceManager.setJson(exportedData);
        }

        return hierarchy;
    }

    private writePropertySheets(urlMap: ReadonlyMap<string, string>): void {
        const prepared = new Map<string, PropertySheet>();
        this._changedPropertySheets = new Set();
        for (const [source, sheet] of this._propertySheets) {
            const path = urlMap.get(source) ?? source;
            const rewritten = repointPropertySheet(sheet, urlMap);
            if (rewritten !== sheet) this._changedPropertySheets.add(preparedAssetKey(path));
            prepared.set(path, rewritten);
            const key = preparedAssetKey(path);
            // Parsed snapshots omit unknown envelope fields. Preserve cached
            // bytes unless a dependency moved or only the snapshot is available.
            if (isLocalAssetPath(path) && (rewritten !== sheet || !this.resourceManager.getAsset(key))) {
                this.resourceManager.addAsset(key,
                    new Blob([JSON.stringify(rewritten)], { type: 'application/json' }));
            }
        }
        this._propertySheets = prepared;
    }

    /**
     * The src a bake just written as `filename` should be named by. When it
     * matches the bake `live` would replace byte for byte, that one: the
     * folder holds it, and it is current again. Otherwise the new file, noted
     * as written, made as of `stamp`.
     */
    private async _settleBake(live: SceneObject | undefined, filename: string, stamp: BakeStamp | undefined): Promise<string> {
        const written = `./assets/${filename}`;
        const from = this._reuseBakesFrom;
        if (!live || !from || !stamp) return written;
        const previous = rememberedBake(live);
        const bytes = this.resourceManager.getAsset(filename);
        if (previous && bytes && await sameBytes(bytes, await bytesOf(from.resources, previous))) {
            keepRememberedBake(live, stamp);
            return previous;
        }
        recordWrittenBake(live, `assets/${filename}`, stamp);
        return written;
    }

    /** The bake to name for `live` instead of baking, when reuse is on (see `unchangedBake`). */
    private _unchangedBake(live: SceneObject | undefined): string | undefined {
        const from = this._reuseBakesFrom;
        if (!live || !from) return undefined;
        const { resources, folderUrl } = from;
        return unchangedBake(live, uri => (folderUrl ? resources.storedUriIn(folderUrl, uri) : undefined));
    }

    /**
     * Bake Bevel-modified primitive nodes into geometry GLB assets.
     *
     * Layout doctrine: the editor bakes; viewers render baked data. A node
     * with a Bevel behavior keeps its params in the scene JSON (the
     * re-edit source of truth — a Bevel-aware editor regenerates over the
     * baked mesh idempotently), while its src is rewritten to a generated
     * GLB of the rounded geometry, so clients with no Bevel implementation
     * render the real shape instead of a sharp primitive.
     *
     * Assets are keyed by (host, params, scale) — identical slabs
     * (repeated cards) dedupe to one GLB. Re-exports of already-baked
     * scenes re-bake from params (both `bevel_<host>_<key>.glb` and
     * rewritten `primitive_<host>.glb` srcs are recognized), so an edited
     * radius never ships a stale mesh. The host primitive (cube or plane) is
     * read off that src, and its `builtin:` src is noted on
     * `Provenance.bakedFrom`, so an authoring load reopens the primitive and
     * its Bevel rather than a Model on the bake. When a deformation bake
     * follows on the same node, that bake writes the note over this one with
     * the Bevel file, its own rig (see recordBakedFrom).
     */
    private async bakeBevelNodes(hierarchy: unknown[], scene: Scene): Promise<void> {
        const generated = new Set<string>();

        const bakeNode = async (record: Record<string, unknown>): Promise<void> => {
            const src = record.src;
            if (typeof src !== 'string') return;

            // Which primitive is under the modifier? A builtin cube/plane,
            // a previously-baked bevel asset, or a rewritten primitive path.
            let host: BevelHostPrimitive | null = null;
            const primitiveType = getPrimitiveType(src);
            if (primitiveType === 'cube' || primitiveType === 'plane') {
                host = primitiveType;
            } else {
                const match = /(?:^|\/)(?:bevel|primitive)_(cube|plane)(?:_[0-9a-f]{8})?\.glb$/.exec(src);
                if (match) host = match[1] as BevelHostPrimitive;
            }
            if (!host) return;

            const behaviors = Array.isArray(record.behaviors)
                ? record.behaviors as Array<Record<string, unknown>>
                : [];
            const bevel = behaviors.find(b => b && b['@type'] === 'Bevel') as BevelGeometryParams | undefined;
            if (!bevel) return;

            const live = this._liveByRecord.get(record as unknown as SceneObjectData);
            const stamp = live && bakeStamp(live);
            let baked = this._unchangedBake(live);
            if (!baked) {
                const transform = record.transform as { scale?: [number, number, number] } | undefined;
                const scaleArr: [number, number, number] = transform?.scale ?? [1, 1, 1];

                const vertexData = generateBevelGeometry(bevel, scaleArr, host);
                if (!vertexData) return; // cornerRadius 0 — the plain primitive is already right

                const key = fnv1aHex(
                    `${host}|r${(bevel.cornerRadius ?? 0).toFixed(6)}|p${bevel.pill ? 1 : 0}|a${bevel.axis ?? 'auto'}`
                    + `|e${bevel.edges ?? 'corners'}|s${scaleArr.map(v => v.toFixed(5)).join(',')}`,
                );
                const filename = bevelBakeFilename(host, key);
                if (!generated.has(filename)) {
                    generated.add(filename);
                    const blob = await generateGeometryGLB(vertexData, filename.replace('.glb', ''), scene);
                    this.resourceManager.addAsset(filename, blob);
                    Logger.log(`[TraceExport] Baked Bevel geometry: ${filename} (${blob.size} bytes)`);
                }
                baked = await this._settleBake(live, filename, stamp);
            }
            // Record the way back before the rewrite, as the morph bake does.
            // The authored source is the host primitive, never the src in
            // hand: re-baking an archive saved before this note existed finds
            // `bevel_<host>_<key>.glb` there, and recording that would name the
            // previous bake as the original.
            recordBakedFrom(record, getBuiltInSrc(host), (ref) => !!this.resourceManager.getAsset(preparedAssetKey(ref)));
            record.src = baked;
            // No Model rides with the bake. One here is the Model a bake src
            // implied when a file saved with the bake as its src was opened
            // (Home's decks carry no note), not something authored: the rig
            // is a primitive, which has none. Saved, it makes an authoring
            // reopen build the primitive AND load the bake (SceneCreation
            // drops it on load for files already saved), and a load that
            // keeps the bake implies it again from the src.
            if (Array.isArray(record.behaviors)) {
                record.behaviors = (record.behaviors as Array<Record<string, unknown>>)
                    .filter(b => b?.['@type'] !== 'Model');
            }
        };

        for (const record of walkExportRecords(hierarchy)) await bakeNode(record);
    }

    /**
     * Bake each extruded Text's body to a geometry asset and point the node's
     * `src` at it, so a player draws the body as plain glTF beside the Text it
     * already draws. The Text data stays the source: the bake records
     * {@link NO_SRC_RIG} as what it was baked from, so an authoring load drops
     * this `src` and the Text builds the body again (`authoringSrc`), and every
     * export bakes it afresh. The file is keyed by its geometry and colour, so
     * identical titles share one.
     *
     * A node whose `src` is something else keeps it, unbaked, with a warning.
     * A text bake already on the node is a player exporting the file it
     * opened (an authoring load drops it), and is kept as it is. A Model on a
     * baked record is the one such a load implied from the bake, and is
     * dropped, as on a Bevel bake.
     */
    private async bakeTextBodies(hierarchy: unknown[], scene: Scene): Promise<void> {
        const generated = new Set<string>();
        for (const record of walkExportRecords(hierarchy)) {
            const text = this._liveByRecord.get(record as unknown as SceneObjectData)?.getBehavior(Text);
            // This Text's own bake, kept as it is (see above); a file that only shares the name is the node's src.
            if (!text || bakedFromRig(record.src, record.behaviors as unknown[] | undefined) === '') continue;
            if (record.src) {
                if (text.isExtruded()) {
                    Logger.warn(`[TraceExport] "${String(record.name)}" has its own src; its extruded text body is not baked`);
                }
                continue;
            }
            const body = await text.buildBody();
            if (!body) {
                if (text.isExtruded()) {
                    Logger.warn(`[TraceExport] "${String(record.name)}" has depth but no body to bake (nothing to outline, or its outlines did not load); it saves as flat text`);
                }
                continue;
            }
            const { geometry, color } = body;
            const filename = textBodyBakeFilename(fnv1aHex(
                `${geometry.positions.map(v => v.toFixed(6)).join(',')}|${geometry.indices.join(',')}|${color.toHexString()}`,
            ));
            if (!generated.has(filename)) {
                generated.add(filename);
                const vertexData = new VertexData();
                vertexData.positions = geometry.positions;
                vertexData.normals = geometry.normals;
                vertexData.indices = geometry.indices;
                const name = filename.replace('.glb', '');
                const blob = await generateGeometryGLB(vertexData, name, scene, {
                    material: exportScene => createTextBodyMaterial(`${name}_material`, exportScene, color),
                });
                this.resourceManager.addAsset(filename, blob);
                Logger.log(`[TraceExport] Baked text body: ${filename} (${blob.size} bytes)`);
            }
            recordBakedFrom(record, NO_SRC_RIG, () => false);
            record.src = `./assets/${filename}`;
            if (Array.isArray(record.behaviors)) {
                record.behaviors = (record.behaviors as Array<Record<string, unknown>>)
                    .filter(b => b?.['@type'] !== 'Model');
            }
        }
    }

    /**
     * Bake morph-deformed nodes to plain-geometry assets (#634).
     *
     * Unified contract for every behavior that drives morph channels
     * (MorphStretch sizing, MeshBackground corner shaping): bake = live
     * morph state → vertices, morphs stripped, behavior DATA kept. For
     * every exported node carrying one of those behaviors whose live
     * meshes have non-zero influences, the morphed vertex positions and
     * normals are baked into the CPU geometry, the subtree (root excluded,
     * so the asset is placement-free) is serialized to a content-keyed GLB
     * with the morph targets stripped, the node's `src` is rewritten to
     * it, and the live meshes are restored untouched.
     *
     * "Live" is the file's view, not the session's: a MorphStretch size or
     * a Renderer material bound to a state- or page-varying property is
     * posed at Base for its bake ({@link showBaseForBake}), because the file
     * opens at Base and a client that runs no behaviors never leaves it.
     *
     * The behavior data deliberately STAYS in the scene JSON — a deviation
     * from the issue's "strip the behavior data" written before #638:
     * post-receiver, a managed node stripped of MorphStretch would be
     * leaf-SCALED by Layout on top of already-target-sized geometry
     * (double-size). With the data kept, both behaviors decline or no-op
     * over baked geometry: MorphStretch knows a bake by its file name
     * (`bakeKindOf`) and sizes it relative to Base, so the scale fallback
     * resolves to identity at Base (its rest already holds any inner
     * overhang and proxy remainder, which sizing from scratch applied
     * twice); MeshBackground finds no `Corner Radius` channel on the baked
     * mesh and declines in `_resolveMeshes`.
     *
     * Re-export of a baked scene is a no-op FOR MORPHS AND SCALE FOLDS —
     * the baked meshes have no targets left to bake, and the folded scale
     * measures at identity (pinned by `scale-sized-bake`). It is NOT a
     * no-op for a triplanar PROJECTION: a projection qualifies a node with
     * zero influences by design, because it re-tiles at rest. So a
     * re-opened baked scene re-qualifies its projected nodes on every
     * export, and since `src` is part of the content key — and `src` is now
     * the previous generation's filename — each export writes a NEW
     * `morphbake_<hash>.glb` and leaves the last one in the archive
     * unreferenced. One dead GLB per projected node per export cycle.
     *
     * The geometry converges (each generation re-projects the same rest
     * positions to the same UVs), so this is churn and archive weight, not
     * corruption. Not fixed here because the obvious skip — "recognize our
     * own `morphbake_` output and decline" — also declines the case that
     * must still work: re-tiling a re-imported component after changing
     * `tileSize`. The honest fix is to drop bundled assets the final
     * records no longer name, which is a collection pass over the whole
     * preparation rather than a rider on the bake. Filed as #725.
     *
     * Triplanar-policy meshes additionally get their UVs re-projected at
     * bake time. The published scene is vanilla glTF — no runtime shader
     * projects anything — so the projection the creator authored (asset
     * extras tag, live editor preview, or the transitional textured-material
     * heuristic; see {@link resolveTriplanarForBake}) must be REALIZED here:
     * on stretched geometry a directional texture smears in every client
     * unless the bake rewrites the UVs. The bake reproduces the editor
     * preview shader's position-in-anchor-rigid-frame mapping (see
     * editor/TriplanarMaterial.ts) on the post-blend vertices
     * (buildProjectedUV: box projection, signed, seam-routed), rebuilds
     * tangents, and re-welds. Within a smart component a resolved projection
     * alone qualifies a node — an UNSTRETCHED node's authored UVs are
     * exactly as wrong as a stretched one's, since the projection re-tiles
     * at rest too, so zero influences is not a reason to skip.
     *
     * The outer gate is still the behavior, not the projection: only nodes
     * carrying MorphStretch or MeshBackground are considered at all. That is
     * deliberate, and it matches the EDITOR — `TriplanarPreview.qualifies`
     * scopes the live preview to exactly those two behaviors, so a static
     * object with a triplanar-tagged material is not projected on screen
     * either. Baking one here would make the export disagree with what the
     * creator saw, which is the opposite of this bake's purpose. Widening
     * the scope is a preview-side decision first; see the pinning test in
     * `triplanar-uv-bake.test.ts`.
     *
     * Scale-sized meshes (MorphStretch's fallback for morphless axes — the
     * whole sizing story for morphless doodads) bake too: their target size
     * lives on mesh.scaling with zero influences, which the morph predicate
     * alone would miss, leaving dumb clients at rest size. The fold moves
     * the sizing from node scaling into the vertices and serializes the
     * REST scaling, so the GLB carries unit-ish transforms with target-size
     * geometry and reload measures rest bounds at the target (factor ≈ 1,
     * no double-scale) — see {@link foldScaleIntoGeometry}.
     *
     * Rest-pose nodes (all influences zero, nothing projected, scaling at
     * rest) are skipped: the original asset already renders correctly
     * everywhere and keeps its morphs for behavior-driven reshaping in
     * behavior-aware viewers.
     */
    private async bakeDeformedNodes(hierarchy: SceneObjectData[], scene: Scene): Promise<void> {
        const generated = new Set<string>();
        // One claim set across every plan, as the scene exporter already does.
        // Nothing overlaps today — `getOwnedMeshes()` stops at nested
        // SceneObject boundaries, so a parent never sees a child's meshes —
        // but that makes this an inference about a boundary defined elsewhere.
        // Sharing the set states it here instead, so the two writers rest on
        // the same guarantee rather than on two readings of it.
        const claimed = new Set<Mesh>();
        const materialDeadline = Date.now() + MATERIAL_SETTLE_TIMEOUT_MS;

        const bakeNode = async (record: Record<string, unknown>): Promise<void> => {
            if (!recordNeedsBake(record.behaviors)) return;
            // No `isSceneFormatRef(record.src)` skip here on purpose: a
            // scene-format wrapper never yields work, because the ownership
            // walk stops at the child objects the nested scene became and the
            // wrapper owns no meshes itself. That is the invariant this rests
            // on — stated, because it is not visible from this line.


            const live = this._liveByRecord.get(record as unknown as SceneObjectData);
            if (!live?.transform) return;

            // Nothing reshaped it since it was opened or stored: name that
            // bake. A projection depends on ancestor transforms no mark
            // follows, so a projected node always bakes.
            const stamp = bakeStamp(live);
            const unchanged = this._unchangedBake(live);
            if (unchanged && typeof record.src === 'string' && new LiveNodeBake(live).projections.size === 0) {
                recordBakedFrom(record, record.src, (ref) => !!this.resourceManager.getAsset(preparedAssetKey(ref)));
                record.src = unchanged;
                return;
            }

            // Bake the size and materials the file opens at, not the state
            // or page on screen: the file carries no active state, so a
            // client that runs no behaviors must get the Base look
            // (normalizePageDecks is the same rule for page flags). Posed
            // before the plan reads the meshes, and baked before the next
            // await, so the key and the bytes both describe Base.
            const showLive = await showBaseForBake(live, materialDeadline);
            try {
                const plan = new LiveNodeBake(live, claimed);
                if (!plan.hasWork) return;

                if (typeof record.src !== 'string' || !record.src) {
                    // The bake delivers its result by rewriting `src` — a node
                    // with baked geometry and no src has nowhere to hang the asset.
                    // Every kind of work gets the warning, morphs included. Gated
                    // on projections/scale only, a MORPH-STRETCHED node with no src
                    // skipped its bake in silence and shipped at rest size — the
                    // most likely case of the three, and the one that looks like
                    // "the size I set did not publish".
                    Logger.warn(`[TraceExport] "${typeof record.name === 'string' ? record.name : '?'}" carries baked geometry (${describeBakeWork(plan)}) but no src — skipping bake, so it publishes at rest`);
                    return;
                }

                // The meshes this bake will SERIALIZE — the same boundary the
                // write below uses, so the key describes exactly what ships.
                const bakedMeshes = plan.exported.filter(
                    (node): node is Mesh => node instanceof Mesh,
                );
                const filename = deformationBakeFilename(fnv1aHex(bakeContentKey(record.src, plan, bakedMeshes)));

                if (!generated.has(filename)) {
                    // Whole-geometry snapshot, not per-buffer copies: the UV
                    // re-projection step un-indexes and re-welds, so the baked
                    // copy's vertex COUNT and index buffer can differ from the
                    // original — a positions/normals restore cannot undo that.
                    // And Babylon clones share Geometry objects, so mutating
                    // buffers in place would bleed into every clone of the mesh;
                    // all bake edits happen on a throwaway Geometry copy instead.
                    try {
                        plan.bake();

                        // Root excluded: children become glTF scene roots with
                        // their root-relative transforms, so the baked GLB is a
                        // placement-free asset — the node's scene transform is
                        // carried by the scene JSON, not baked in twice.
                        //
                        // The set stops at nested SceneObjects (and drops editor
                        // helper meshes): a child object exports as its own
                        // record with its own src, so serializing it in here too
                        // renders it twice on reload — once from the baked parent
                        // asset, once from its own record.
                        const nodes = plan.exportedSet;
                        warnHelperMeshExcluded(`TraceExport bake ${filename}`, plan.excludedHelpers);
                        const blob = await serializeNodesToGlb(scene, filename, nodes);
                        this.resourceManager.addAsset(filename, blob);
                        generated.add(filename);
                        Logger.log(`[TraceExport] Baked morph-deformed geometry: ${filename} (${blob.size} bytes)`);
                    } finally {
                        plan.restore();
                    }
                }
                // Record the way back BEFORE the rewrite, or the original is
                // gone: the baked GLB carries no morph rig, so without this a
                // re-opened component could never be re-sized. The note lands on
                // a per-object `Provenance` behavior rather than a top-level
                // field, so it rides the loader hand-offs `behaviors[]` already
                // makes and degrades to `GenericBehavior` in a client that has
                // never heard of it.
                //
                // The note names the src this bake replaced — the live one, so a
                // form swapped in since the last save is what the next re-open
                // gets. A bake of a bake keeps the note it arrived with.
                recordBakedFrom(record, record.src, (ref) => !!this.resourceManager.getAsset(preparedAssetKey(ref)));
                record.src = await this._settleBake(live, filename, stamp);
            } finally {
                await showLive();
            }
        };

        // Flat, not recursive: `_liveByRecord` is keyed by the records of the
        // flat hierarchy array, so only those can ever resolve a live object.
        // Descending into their sub-objects (transform, behaviors) only ever
        // found records that miss the map.
        for (const record of hierarchy) {
            await bakeNode(record as unknown as Record<string, unknown>);
        }
    }

    /**
     * Export the scene and return the zip as a Blob.
     *
     * @param scene - Babylon.js scene
     * @param sceneName - Name for the exported scene
     * @param infoJson - When provided, produces .zip trace scene format (models.json + info.json).
     *                   When undefined, produces .trace format (scene.json).
     * @param traceScene - TraceScene instance for accessing scene objects
     * @param options - See {@link TraceExportSceneOptions}.
     */
    public async exportScene(scene: Scene, sceneName: string, infoJson: TraceSceneInfoFile | undefined, traceScene?: TraceScene, options?: TraceExportSceneOptions): Promise<Blob> {
        await this.prepareExport(scene, sceneName, infoJson, traceScene, options);
        if (traceScene) await this.bakeLegacyGlbRoots(traceScene, options?.subtreeRoot, options?.exclude);
        return this.resourceManager.createTraceBlob(options?.onProgress);
    }

    /**
     * Rewrite bundled GLBs whose root the runtime discarded, so the archive
     * stands on its own.
     *
     * The archive is written as version 2.0.0, and v2 PRESERVES GLB roots — so
     * a legacy scene (whose roots were discarded on load because Unity had
     * baked them into the scene transform) would re-import with both applied.
     * Same defect and same fix as the `.tracex` path; see export/glbRootBake.ts
     * for why this is a bake rather than a marker.
     */
    private async bakeLegacyGlbRoots(
        traceScene: TraceScene,
        subtreeRoot: SceneObject | undefined,
        exclude: ExcludedSubtrees | undefined,
    ): Promise<void> {
        // Scoped like the capture (spec §2.9): a legacy model only an excluded
        // node names was never packed, so there is nothing to bake for it.
        const scope = withoutExcluded(
            subtreeRoot
                ? [subtreeRoot, ...subtreeRoot.getDescendants()]
                : traceScene.getSceneObjects(),
            exclude,
        );
        const legacy = scope.filter(object => object._resetAssetTransform && object.src);
        if (legacy.length === 0) return;

        const { bakeGlbRootIdentity } = await import('./glbRootBake');
        const done = new Set<string>();
        for (const object of legacy) {
            // Assets were re-homed under a flat `assets/` name during packing.
            const name = ResourceManager.removeAssetsPrefix(object.src);
            if (done.has(name)) continue;
            done.add(name);

            const blob = this.resourceManager.getAsset(name) ?? this.resourceManager.getAsset(`assets/${name}`);
            if (!blob) continue;
            const baked = bakeGlbRootIdentity(new Uint8Array(await blob.arrayBuffer()));
            if (baked) this.resourceManager.addAsset(name, new Blob([baked.slice().buffer]));
        }
    }
}

/**
 * The legacy archive's view of one record: everything except the glTF 2.1 node
 * identity (issue #837, SCENE_ROOT_SPEC §2.3) and `TRACE_animation_binding`.
 *
 * `uid` is a 2.1 concept and the `.trace`/`.zip` writers are frozen — they
 * emit exactly what they always did, so a legacy file written from a 2.1
 * session carries no field a legacy reader has never seen. The PREPARED
 * records keep theirs: the folder writer reads the same preparation and must
 * write `node.uid` (one preparation, two packagers).
 *
 * A record without a uid is returned as-is, so a legacy session's bytes are
 * identical to before, key order included.
 */
function withoutNodeUid(record: SceneObjectData): SceneObjectData {
    if (record.uid === undefined && record.animationBinding === undefined) return record;
    // `animationBinding` (TRACE_animation_binding) is 2.1-document-only too: a
    // legacy archive has no `externalAssets` for its sources to index.
    const { uid: _uid, animationBinding: _binding, ...rest } = record;
    return rest;
}

/**
 * Every object reachable in an export hierarchy, depth-first: the node
 * records themselves plus each nested object inside them (transform,
 * behaviors, behavior sub-objects, arrays). The Bevel bake, the external-URL
 * scan and the URL rewrite all need exactly this traversal — one helper so a
 * field reachable by one pass can't be invisible to another.
 *
 * Arrays are yielded too: a string ELEMENT can be an asset ref
 * (`Properties.sheets[]`), so passes that rewrite string values must see them.
 * Generation history fields are not entered.
 */
function walkExportRecords(hierarchy: readonly unknown[]): Array<Record<string, unknown>> {
    const records: Array<Record<string, unknown>> = [];
    const visit = (value: unknown): void => {
        if (!value || typeof value !== 'object') return;
        const record = value as Record<string, unknown>;
        records.push(record);
        for (const key of Object.keys(record)) {
            // Generation history is skipped; every other field is walked — `Provenance.bakedFrom` names an asset that may exist
            // only in this archive, so it is collected and rewritten like any
            // other reference.
            if (isProvenanceHistoryField(record, key)) continue;
            visit(record[key]);
        }
    };
    for (const node of hierarchy) visit(node);
    return records;
}

/** The bytes `resources` holds or can fetch for `src`, or undefined. */
async function bytesOf(resources: ResourceManager, src: string): Promise<Blob | undefined> {
    const held = resources.getAsset(src) ?? resources.getAsset(src.replace(/^\.\//, ''));
    if (held) return held;
    const url = resources.resolveAssetUrl(src);
    if (!url) return undefined;
    try {
        const response = await fetch(url);
        return response.ok ? await response.blob() : undefined;
    } catch (error) {
        Logger.warn(`[TraceExport] could not read ${src} to compare a re-bake with it`, error);
        return undefined;
    }
}

async function sameBytes(a: Blob, b: Blob | undefined): Promise<boolean> {
    if (!b || a.size !== b.size) return false;
    const [x, y] = await Promise.all([a.arrayBuffer(), b.arrayBuffer()]);
    const left = new Uint8Array(x);
    const right = new Uint8Array(y);
    return left.every((byte, i) => byte === right[i]);
}

/**
 * Serialize a set of Babylon nodes to a GLB, with the two guards every bake
 * site needs.
 *
 * Hand-rolled in both writers, it drifted: the helper filter, the
 * phantom-animation block and the `exportUnusedUVs` decision are one
 * contract, and a fix applied to one copy silently left the other wrong.
 *
 * - **Phantom animations.** `GLTF2Export` serializes every AnimationGroup in
 *   the scene regardless of `shouldExportNode`. A track whose target is
 *   filtered out is emitted with `target.node` undefined — Babylon ignores
 *   those per spec, Unity binds them to a fallback node and animates the
 *   wrong thing. Targets may be Bones, which are not in the scene graph, so
 *   membership resolves through the linked TransformNode.
 * - **`exportUnusedUVs`, always.** The exporter keeps only the UV sets the
 *   exported material samples, and the material a bake writes is rarely the
 *   one players draw. A triplanar-projected material samples through the
 *   PLUGIN, so its slots can be empty while the baked UVs are the entire point
 *   of the asset. And the bake is posed at Base: a state's textured material
 *   (or a Style's) is put on by the Renderer over the baked mesh, and without
 *   UVs it renders as one flat colour.
 */
export async function serializeNodesToGlb(
    scene: Scene,
    fileName: string,
    nodes: ReadonlySet<Node>,
): Promise<Blob> {
    const blocked = new Set<BabylonAnimation>();
    for (const group of scene.animationGroups) {
        for (const targeted of group.targetedAnimations) {
            if (!isAnimationTargetInSet(targeted.target, nodes)) {
                blocked.add(targeted.animation);
            }
        }
    }
    await whenTexturesReady(nodes);
    const gltf = await GLTF2Export.GLBAsync(scene, fileName, {
        // Not the whole scene: see whenTexturesReady.
        exportWithoutWaitingForScene: true,
        shouldExportNode: (node: Node) => nodes.has(node),
        shouldExportAnimation: (animation: BabylonAnimation) => !blocked.has(animation),
        exportUnusedUVs: true,
    });
    return gltf.glTFFiles[fileName] as Blob;
}

/**
 * Wait for the textures the serialized meshes carry, and nothing else.
 *
 * `GLBAsync` on its own waits for `scene.whenReadyAsync()` — the whole scene.
 * One entry anywhere that never leaves the scene's pending data (a Studio
 * session was seen holding its environment map's texture there for good)
 * then holds every bake open forever, with the live meshes still on their
 * bake geometry, and every save with a stretched component never returns.
 * Bounded for the same reason: past the limit the GLB is written with what
 * has loaded.
 */
export async function whenTexturesReady(nodes: ReadonlySet<Node>, timeoutMs = 10_000): Promise<void> {
    const textures = new Set<BaseTexture>();
    for (const node of nodes) {
        const material = (node as { material?: Material | null }).material;
        const materials = material instanceof MultiMaterial ? material.subMaterials : [material];
        for (const m of materials) for (const t of m?.getActiveTextures() ?? []) textures.add(t);
    }
    const deadline = Date.now() + timeoutMs;
    let pending = [...textures].filter(t => !t.isReady());
    while (pending.length > 0) {
        if (Date.now() >= deadline) {
            Logger.warn(`[TraceExport] bake: ${pending.length} texture(s) still loading after ${timeoutMs} ms, writing without them: ${pending.map(t => t.name).join(', ')}`);
            return;
        }
        await new Promise(resolve => setTimeout(resolve, 50));
        pending = pending.filter(t => !t.isReady());
    }
}

/** Tiny stable content key for baked-geometry asset names. */
function fnv1aHex(input: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Note a node's authored `src` on its `Provenance` behavior, before the bake
 * rewrites it — see `ProvenanceData.bakedFrom`.
 *
 * When that src is itself a bake, a note it arrived with names the original
 * and is kept — a re-tiled projection on a re-published file, or a Bevel bake
 * whose pass noted the primitive first. With no note, the kind decides: a
 * deformation bake is geometry, not a way back, so nothing is recorded; a
 * Bevel bake is a rounded primitive with no morphs, which MorphStretch sizes
 * again by scale, so it is recorded as the way back. Any other src arrives
 * without a note ({@link retireStaleBakedFrom}), so the note written here is
 * the src's own.
 *
 * `bundled` answers whether this preparation carries a local ref's bytes.
 */
function recordBakedFrom(
    record: Record<string, unknown>,
    authoredSrc: string,
    bundled: (ref: string) => boolean,
): void {
    const behaviors = Array.isArray(record.behaviors)
        ? record.behaviors as Array<Record<string, unknown>>
        : [];
    const existing = behaviors.find((b) => b && b['@type'] === 'Provenance');
    const bake = bakeKindOf(authoredSrc);
    // A src that is already a deformation bake is a republish: its note still
    // names the rig, so it stays. A Bevel bake is different: it is the rig of
    // the deformation bake that follows it in the same export, so the note
    // becomes the Bevel file, and reopening gives back the card, stretchable
    // again. The primitive the Bevel replaced is remembered only while the
    // Bevel bake is the last one on the node.
    if (bake === 'deformation') return;
    // Only record a way back that actually leads somewhere. The note is
    // FETCHED on an authoring load, so naming an asset this export neither
    // bundled nor left hosted produces a restore that swaps in a src which
    // 404s — worse than not restoring, because the scene looks re-editable
    // right up until it is empty. An unbundled local ref means the original
    // is not travelling, so there is nothing honest to point at. A `builtin:`
    // primitive always travels: every Trace engine generates it on load
    // (SceneCreation.applyComponents), so there are no bytes to carry.
    const travels = isBuiltInPrimitive(authoredSrc) || isAbsoluteHttpUrl(authoredSrc) || bundled(authoredSrc);
    if (!travels) {
        Logger.warn(`[TraceExport] "${typeof record.name === 'string' ? record.name : '?'}" baked from "${authoredSrc}", which this export does not carry — publishing without a way back`);
        return;
    }
    if (existing) {
        existing.bakedFrom = authoredSrc;
        return;
    }
    behaviors.push({ '@type': 'Provenance', bakedFrom: authoredSrc });
    record.behaviors = behaviors;
}

/**
 * Drop `Provenance.bakedFrom` from a record whose src is not a bake.
 *
 * The note says "this src was baked from X": true of the bake it was saved
 * beside, false of any src set since — a form swap, a filled placeholder, an
 * undo — and an authoring load would open X in place of what the author
 * chose. Beside a bake it stays. Run before assets are collected, so X's bytes
 * do not ride along either; a record that bakes gets a fresh note from
 * {@link recordBakedFrom}.
 *
 * The rest of the Provenance record stays. One left holding nothing goes.
 */
function retireStaleBakedFrom(record: SceneObjectData): void {
    if (isBakedGeometrySrc(record.src) || !record.behaviors) return;
    const kept: BehaviorData[] = [];
    for (const behavior of record.behaviors) {
        if (behavior['@type'] !== 'Provenance' || !('bakedFrom' in behavior)) {
            kept.push(behavior);
            continue;
        }
        const { bakedFrom: _stale, ...rest } = behavior as BehaviorData & { bakedFrom?: unknown };
        if (Object.keys(rest).some(key => key !== '@type')) kept.push(rest);
    }
    record.behaviors = kept;
}

/** What a plan would have baked, for the no-src diagnostic. */
function describeBakeWork(plan: LiveNodeBake): string {
    const parts: string[] = [];
    if (plan.morphed.length > 0) parts.push(`${plan.morphed.length} morph-stretched mesh(es)`);
    if (plan.projections.size > 0) parts.push(`${plan.projections.size} triplanar projection(s)`);
    if (plan.scaled.size > 0) parts.push(`${plan.scaled.size} scale fold(s)`);
    return parts.join(', ') || 'no work';
}

/**
 * A signature of what a mesh's material will SERIALIZE as.
 *
 * The bake writes a real GLB, and `GLTF2Export` puts each mesh's live material
 * and textures in it — so the material is part of the file's content whether
 * or not it is part of the deformation. Keying only on geometry inputs made
 * two placements of one asset at one solved size collide: the duplicated-card
 * case where one card carries a Renderer tint. They shared a
 * `morphbake_<hash>.glb`, and the loser's `src` pointed at a GLB wearing the
 * winner's material — with nothing to correct it in any client if the loser
 * carried no Renderer data of its own, and which node won decided by
 * hierarchy order.
 *
 * By VALUE rather than by object identity: two distinct materials with the
 * same appearance serialize to the same bytes and should still share one file.
 * Identity would also churn the filename every session, since `uniqueId` is
 * assigned per load.
 *
 * Unknown material classes fall back to the name, which is what the bake used
 * to rely on entirely.
 */
function materialContentKey(material: Material | null): string {
    if (!material) return '-';
    // Hash what the material SERIALIZES as, not a hand-picked list of fields.
    //
    // A hand list is a shadow of the serializer, and everything it does not
    // name is invisible to the key: texture transforms (uScale/vOffset/wAng),
    // alphaMode and cutoff, backFaceCulling, clearcoat/sheen/iridescence, a
    // MultiMaterial's sub-materials, and procedural textures whose name and
    // url are both empty. Two materials differing only there would collide
    // onto one `morphbake_<hash>.glb` — the exact wrong-material swap this
    // key exists to prevent, in the duplicated-card case where one card
    // carries a Renderer tint.
    //
    // `serialize()` is Babylon's own account of the material, so it tracks
    // whatever the serializer grows next without this function knowing.
    //
    // Known churn, not a correctness problem: the glTF loader salts texture
    // URLs with `Date.now()` for blob-loaded scenes, so a local-file workflow
    // gets fresh morphbake filenames each session.
    try {
        const parts: string[] = [material.getClassName()];
        const sub = (material as unknown as { subMaterials?: (Material | null)[] }).subMaterials;
        if (Array.isArray(sub)) {
            // A MultiMaterial serializes as a list of NAMES; the appearance
            // lives in the sub-materials, so key on those.
            for (const child of sub) parts.push(materialContentKey(child ?? null));
        }
        parts.push(stableStringify(material.serialize() as unknown));
        return fnv1aHex(parts.join('~'));
    } catch (err) {
        // A material that cannot serialize must not take the export down; fall
        // back to identity, which over-splits (more files) rather than
        // colliding (wrong bytes).
        Logger.warn(`[TraceExport] material "${material.name}" did not serialize for the bake key; keying by identity`, err);
        return `id:${material.uniqueId}`;
    }
}

/**
 * JSON with object keys in sorted order, so two equal materials hash equal.
 * `JSON.stringify` preserves insertion order, which Babylon does not promise
 * to keep stable between two objects built by different paths.
 */
function stableStringify(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([key, v]) => v !== undefined && !INSTANCE_IDENTITY.has(key))
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

/**
 * Serialized fields that say WHICH object this is rather than what it looks
 * like. Two materials built the same way differ here and nowhere else, and
 * the bake key is about appearance — keeping them would give every placement
 * its own baked file, which is the dedup this key exists for, inverted.
 */
const INSTANCE_IDENTITY = new Set(['uniqueId', 'id']);

/**
 * Content key for a node's baked GLB: influence vector + projection signature
 * + scale folds + material identity — identical placements of one asset at
 * one solved size share a single baked file.
 *
 * `tri1` is the projector algorithm version: bump it whenever
 * {@link buildProjectedUV}'s mapping changes, so new bakes split from
 * previously exported assets instead of silently colliding. The projMatrix
 * hash captures ancestor-scale dependence — two placements of one asset under
 * different ancestor scales legitimately produce different GLBs.
 */
function bakeContentKey(src: string, plan: LiveNodeBake, baked: readonly Mesh[]): string {
    const morphSegment = plan.morphed.map((mesh) => {
        const mgr = mesh.morphTargetManager!;
        const parts: string[] = [mesh.name];
        for (let i = 0; i < mgr.numTargets; i++) {
        parts.push(`${mgr.getTarget(i).name}:${mgr.getTarget(i).influence.toFixed(6)}`);
        }
        return parts.join(',');
    }).join(';');
    // One segment per PROJECTED range; a single-range mesh omits the range
    // marker.
    const projSegment = [...plan.projections].map(([mesh, proj]) =>
        proj.ranges.map((range, i) => {
            if (!range.config) return '';
            const tag = proj.ranges.length > 1 ? `#${i}` : '';
            return `|tri1:${mesh.name}${tag}:${range.config.tileSize}:${range.config.sharpness}`
                + `:${fnv1aHex(Array.from(range.projMatrix!.m, (v) => Math.round(v * 1e5) / 1e5).join(','))}`;
        }).join(''),
    ).join('');
    const scaleSegment = [...plan.scaled.keys()].map((mesh) => {
        const s = mesh.scaling;
        const r = (v: number): number => Math.round(v * 1e5) / 1e5;
        return `|scl:${mesh.name}:${r(s.x)},${r(s.y)},${r(s.z)}`;
    }).join('');
    // Sorted by mesh name so traversal order cannot change the key for
    // identical content.
    const materialSegment = baked
        .map((mesh) => `${mesh.name}=${materialContentKey(mesh.material)}`)
        .sort()
        .join(',');
    return src + '|' + morphSegment + projSegment + scaleSegment + '|mat:' + materialSegment;
}

/**
 * Mesh → the material name Renderer captured BEFORE it swapped a slot
 * material in ({@link Renderer.getOriginalMaterials}, keyed by index into
 * {@link SceneObject.getOwnedMeshes} — the walk Renderer captures against, so
 * the lookup MUST use that same one. A bare `getObjectMeshes` walk is a
 * DIFFERENT list: it does not stop at child SceneObjects, so one mesh-bearing
 * child slides every later index and the names land on the wrong meshes —
 * silently, since the bake still writes a valid GLB, just at the wrong tile
 * density).
 *
 * The bake needs it because family tuning is name-based: a Renderer tint
 * clone is named for its slot ("Sofa_mat0"), not its library family, and the
 * editor preview resolves with both names. Empty map when the object has no
 * Renderer — every mesh then tunes on its own name, as before.
 */
function recordedMaterialNames(object: SceneObject): Map<Mesh, string> {
    const names = new Map<Mesh, string>();
    const renderer = object.getBehavior(Renderer);
    const recorded = renderer?.getOriginalMaterials();
    if (!renderer || !recorded) return names;
    // Ask the Renderer which mesh each slot actually holds. Resolving the slot
    // INDEX against a fresh walk is the positional lookup Renderer's own code
    // documents as unsound and keeps `_meshSlots` to replace: a reload or a
    // disposed sub-mesh shifts the walk, and a family name lands on the wrong
    // mesh — a valid-looking GLB at the wrong tile density.
    const slots = renderer.getMeshSlots();
    for (const [index, name] of Object.entries(recorded)) {
        const mesh = slots.get(Number(index));
        if (mesh instanceof Mesh && name) names.set(mesh, name);
    }
    return names;
}

/**
 * Vertex streams the projection rewrite carries through un-index → weld.
 * A mesh with any other stream (colors, UV2, skin weights) gates out of
 * projection: the weld changes the vertex count, and a stream it doesn't
 * rebuild would be serialized at the stale count — a corrupt GLB.
 */
const PROJECTABLE_KINDS = new Set<string>([
    VertexBuffer.PositionKind,
    VertexBuffer.NormalKind,
    VertexBuffer.UVKind,
    VertexBuffer.TangentKind,
]);

/**
 * The streams Babylon mirrors a MorphTargetManager into on an engine that
 * cannot keep targets in a texture (NullEngine, so every headless export):
 * `position0`, `normal0`, `tangent0`, `uv_0`, `uv2_0`, `color0`, one set per
 * target. The bake removes the manager before it projects and Babylon drops
 * these with it, so they never reach the weld.
 */
const MORPH_TARGET_STREAM = /^(?:position|normal|tangent|color)\d+$|^(?:uv|uv2)_\d+$/;

/**
 * One submesh range of a projection plan. Ranges address the ORIGINAL index
 * buffer (captured at plan time so the bake's geometry swaps cannot shift
 * them). A range without a config belongs to a multi-material mesh whose
 * OTHER ranges project — it rides through the rebuild with authored UVs.
 */
interface RangeProjection {
    /** Serialized back onto the rebuilt submesh — picks the sub-material. */
    materialIndex: number;
    /** This range's slice of the original index buffer. */
    indexStart: number;
    indexCount: number;
    /** Resolved projection config (see resolveTriplanarForBake), or null → keep authored UVs. */
    config: TriplanarConfig | null;
    /** mesh-local → anchor-rigid frame: the space the editor preview shader samples in. */
    projMatrix: Matrix | null;
    /** Inverse-transpose of `projMatrix`, for normals. */
    normalMatrix: Matrix | null;
}

interface MeshProjection {
    /** Per-submesh ranges in submesh order; at least one carries a config. */
    ranges: RangeProjection[];
}


/**
 * The behaviors whose live state a bake has to realize.
 *
 * One list, because it is asked in two encodings: the record walk only has
 * serialized `@type` strings, and the object walk has live instances. Split
 * across two sites, the next geometry-deforming behavior gets added to one of
 * them and assets quietly ship at rest shape with no error anywhere.
 */
const BAKED_BEHAVIOR_TYPES = ['MorphStretch', 'MeshBackground'] as const;

/** Does this SERIALIZED record carry a behavior the bake realizes? */
export function recordNeedsBake(behaviors: unknown): boolean {
    return Array.isArray(behaviors)
        && behaviors.some(b => !!b && typeof b === 'object'
            && (BAKED_BEHAVIOR_TYPES as readonly string[]).includes(
                String((b as Record<string, unknown>)['@type'])));
}

/** Does this LIVE object carry one? */
export function objectNeedsBake(object: SceneObject): boolean {
    return !!object.getBehavior(MorphStretch) || !!object.getBehavior(MeshBackground);
}

/**
 * Pose a live object as its file opens, at Base, for a bake: the Renderer's
 * bound materials, then the MorphStretch size. The file carries no active
 * state and no page assignment, so a bake of whatever is on screen would ship
 * the state as the component's only look. Materials go first because they
 * decide whether a slot needs UV re-projection, so they must be in place
 * before a plan reads the meshes. The size is posed last and synchronously,
 * so a caller that plans and bakes right after the await reads Base geometry.
 * Returns the way back to the session's view, for a `finally`. If the size
 * pose throws, the materials are put back before the error propagates, since
 * the caller gets no way back from a throw. `materialDeadline` (epoch ms) is
 * the export's one deadline for material loads already in flight, shared by
 * every object it poses (see `MATERIAL_SETTLE_TIMEOUT_MS`).
 */
export async function showBaseForBake(object: SceneObject, materialDeadline?: number): Promise<() => Promise<void>> {
    const showLiveMaterials = await object.getBehavior(Renderer)?.showBaseMaterialsForBake(materialDeadline) ?? null;
    let showLiveSize: (() => void) | null;
    try {
        showLiveSize = object.getBehavior(MorphStretch)?.showBaseSizeForBake() ?? null;
    } catch (error) {
        await showLiveMaterials?.();
        throw error;
    }
    return async () => {
        showLiveSize?.();
        await showLiveMaterials?.();
    };
}

/**
 * Plan-and-execute container for baking one live node's meshes into their
 * DISPLAYED state: morph influences blended into vertices, triplanar
 * projections rewritten into TEXCOORD_0 (+ tangents), scale-fallback
 * sizing folded into geometry. Shared by every export writer — the
 * .trace/.tracex asset bake and the object-level GLB export must ship the
 * same bytes. `bake()` mutates the meshes onto throwaway geometries;
 * `restore()` puts the live scene back exactly (call it in `finally`).
 */
export class LiveNodeBake {
    /** Every Babylon node this object ships — the one ownership walk. */
    readonly exported: Node[];
    /** {@link exported} as a set, for membership questions. */
    readonly exportedSet: ReadonlySet<Node>;
    /** Helper meshes the walk dropped, for the caller that reports them. */
    readonly excludedHelpers: Node[];
    morphed: Mesh[];
    projections: Map<Mesh, MeshProjection>;
    scaled: Map<Mesh, { rest: Vector3; factor: Vector3 }>;
    private _candidates: Mesh[];
    private _snapshots: Array<{
        mesh: Mesh;
        geometry: Geometry;
        manager: MorphTargetManager | null;
        scaling: Vector3;
        subMeshes: Array<{
            materialIndex: number;
            verticesStart: number;
            verticesCount: number;
            indexStart: number;
            indexCount: number;
        }>;
    }> = [];

    /**
     * @param live - the object whose displayed state this plan bakes.
     * @param claimed - meshes an EARLIER plan in the same export already owns.
     *   Plans are disjoint by construction (candidates stop at nested
     *   SceneObjects), but a shared claim set makes that a guarantee rather
     *   than an inference: two plans holding one mesh means the second
     *   snapshots the first's throwaway geometry and its restore disposes the
     *   real thing. First claim wins; claimed meshes are added as they're taken.
     *
     * Disjoint is only half of it — the plans must also COVER everything the
     * runtime deformed, or a mesh nothing plans ships at rest while the screen
     * shows it stretched. That holds because the behaviors deform through the
     * same boundary this walk stops at: MorphStretch resolves its meshes with
     * SceneObject.getOwnedMeshes(), so a child object bounds deformation
     * exactly as ownedExportNodes bounds the bake, and a child that stretches
     * carries its own MorphStretch and so gets its own plan. Widening either
     * side alone reopens the gap
     * (test/vitest/behaviors/morphstretch-mesh-ownership.test.ts).
     */
    constructor(readonly live: SceneObject, claimed?: Set<Mesh>) {
        // ONE ownership walk per plan, kept for every later consumer.
        //
        // `ownedExportNodes` resolves each descendant through
        // `findSceneObjectByTransform`, whose cache is positive-only, so every
        // GLB-internal mesh costs a linear scan over all SceneObjects. Walking
        // three or four times per baked node put that on the main thread
        // during save, multiplied by scene size.
        this.excludedHelpers = [];
        this.exported = ownedExportNodes(this.live, this.excludedHelpers);
        this.exportedSet = new Set<Node>(this.exported);
        // Bake plan. Candidates: non-instance meshes this object owns —
        // an instance shares its source's manager and geometry and can own
        // neither (MorphStretch assets load cloned since #636 anyway), and a
        // mesh under a nested SceneObject belongs to that object's own plan.
        const candidates = this.exported.filter(
            (n: Node): n is Mesh => n instanceof Mesh
                && !(n as { isAnInstance?: boolean }).isAnInstance
                && !claimed?.has(n),
        );
        for (const mesh of candidates) claimed?.add(mesh);
        this._candidates = candidates;

        // Meshes whose live influences differ from rest.
        this.morphed = candidates.filter((mesh) => {
            const mgr = mesh.morphTargetManager;
            if (!mgr) return false;
            for (let i = 0; i < mgr.numTargets; i++) {
                if (Math.abs(mgr.getTarget(i).influence) > 1e-6) return true;
            }
            return false;
        });

        // Meshes with any material range (per-submesh, for MultiMaterial)
        // resolving a triplanar projection policy — asset tag, live editor
        // preview, or textured heuristic — their UVs need re-projecting
        // whether or not morphs moved any vertices (the projection applies
        // at rest size too). The object root anchors tag/heuristic ranges,
        // exactly where the editor preview anchors its plugins.
        //
        // Renderer's recorded pre-swap material names ride along: behind a
        // renamed Renderer clone, tuning by the clone's own name alone finds
        // no family and bakes DEFAULT density where the preview — which also
        // reads the recorded original — used the library's.
        this.projections = planProjections(
            candidates,
            this.live.transform,
            recordedMaterialNames(this.live),
        );

        // Meshes sized by MorphStretch's scale fallback (a morphless
        // doodad with an authored size, or a layout-resolved box on a
        // morphless axis): the size lives on mesh.scaling with ZERO
        // morph influences, so the morph predicate alone would skip the
        // node and dumb clients would render rest size. Rest scaling
        // comes from the behavior's own snapshot (getRestScaling) — the
        // one place it was captured before anything mutated the mesh.
        const stretch = this.live.getBehavior(MorphStretch);
        this.scaled = new Map<Mesh, { rest: Vector3; factor: Vector3 }>();
        if (stretch) {
            for (const mesh of candidates) {
                const rest = stretch.getRestScaling(mesh);
                if (!rest) continue;
                const cur = mesh.scaling;
                if (Math.abs(cur.x - rest.x) <= 1e-6
                    && Math.abs(cur.y - rest.y) <= 1e-6
                    && Math.abs(cur.z - rest.z) <= 1e-6) continue;
                if (Math.abs(rest.x) <= 1e-9 || Math.abs(rest.y) <= 1e-9 || Math.abs(rest.z) <= 1e-9) {
                    Logger.warn(`[TraceExport] scalebake ${mesh.name}: degenerate rest scaling — skipping scale fold`);
                    continue;
                }
                // Same guard on the CURRENT scaling, and NOT just against
                // zero. Zero divides normals into NaN; a NEGATIVE axis is
                // worse, because it succeeds: the factor mirrors positions,
                // which flips triangle winding, while the normals — divided
                // by that same negative and renormalized — stay consistent
                // with the OLD winding, and tangent handedness is not flipped
                // either. The GLB is valid and renders inside-out in every
                // consumer, after a clean export. MorphStretch cannot emit
                // either value, but the Inspector edit this guard already
                // exists for reaches both.
                if (!(cur.x > 1e-9) || !(cur.y > 1e-9) || !(cur.z > 1e-9)) {
                    Logger.warn(`[TraceExport] scalebake ${mesh.name}: current scaling is zero, negative or NaN (${cur.x}, ${cur.y}, ${cur.z}) — skipping scale fold`);
                    continue;
                }
                this.scaled.set(mesh, {
                    rest,
                    factor: new Vector3(cur.x / rest.x, cur.y / rest.y, cur.z / rest.z),
                });
            }
        }
    }

    /** Whether any mesh needs baking at all. */
    get hasWork(): boolean {
        return this.morphed.length > 0 || this.projections.size > 0 || this.scaled.size > 0;
    }

    bake(): void {
        const morphedSet = new Set(this.morphed);
        const bakeTargets = [
            ...this.morphed,
            ...this._candidates.filter((m) =>
                !morphedSet.has(m) && (this.projections.has(m) || this.scaled.has(m))),
        ];
        for (const mesh of bakeTargets) {
            const geometry = mesh.geometry;
            if (!geometry) continue;
            const base = mesh.getVerticesData(VertexBuffer.PositionKind);
            if (!base) continue;
            const mgr = morphedSet.has(mesh) ? mesh.morphTargetManager! : null;
            let baked: Float32Array | null = null;
            let bakedNormals: Float32Array | null = null;
            if (mgr) {
                const baseNormals = mesh.getVerticesData(VertexBuffer.NormalKind);
                baked = new Float32Array(base);
                bakedNormals = baseNormals ? new Float32Array(baseNormals) : null;
                for (let t = 0; t < mgr.numTargets; t++) {
                    const target = mgr.getTarget(t);
                    const influence = target.influence;
                    if (Math.abs(influence) <= 1e-6) continue;
                    // Babylon targets store ABSOLUTE positions (the
                    // glTF loader adds deltas onto the base).
                    const targetPositions = target.getPositions();
                    if (targetPositions) {
                        for (let i = 0; i < baked.length; i++) {
                            baked[i] += influence * (targetPositions[i] - base[i]);
                        }
                    }
                    const targetNormals = bakedNormals && target.hasNormals ? target.getNormals() : null;
                    if (targetNormals && bakedNormals && baseNormals) {
                        for (let i = 0; i < bakedNormals.length; i++) {
                            bakedNormals[i] += influence * (targetNormals[i] - baseNormals[i]);
                        }
                    }
                }
                if (bakedNormals) {
                    for (let i = 0; i < bakedNormals.length; i += 3) {
                        const x = bakedNormals[i], y = bakedNormals[i + 1], z = bakedNormals[i + 2];
                        const len = Math.hypot(x, y, z) || 1;
                        bakedNormals[i] = x / len;
                        bakedNormals[i + 1] = y / len;
                        bakedNormals[i + 2] = z / len;
                    }
                }
            }
            this._snapshots.push({
                mesh, geometry,
                manager: mesh.morphTargetManager,
                scaling: mesh.scaling.clone(),
                // Ranges, not SubMesh objects: the projection
                // rebuild replaces the submesh array, and the
                // restore recreates it from these numbers.
                subMeshes: mesh.subMeshes.map((sm: SubMesh) => ({
                    materialIndex: sm.materialIndex,
                    verticesStart: sm.verticesStart,
                    verticesCount: sm.verticesCount,
                    indexStart: sm.indexStart,
                    indexCount: sm.indexCount,
                })),
            });
            geometry.copy(RandomGUID()).applyToMesh(mesh);
            if (baked) mesh.setVerticesData(VertexBuffer.PositionKind, baked, true);
            if (bakedNormals) mesh.setVerticesData(VertexBuffer.NormalKind, bakedNormals, true);
            const proj = this.projections.get(mesh);
            const sc = this.scaled.get(mesh);
            // Morph-baked meshes must not also serialize their
            // (already applied) targets; projected meshes must not
            // serialize targets whose vertex count the weld below
            // is about to change out from under them; and a
            // scale-folded mesh must not either — the fold multiplies
            // the BASE positions and leaves the targets alone, so the
            // serializer would write `delta = target − folded_base`
            // and any consumer raising that influence would snap
            // toward the unfolded rest shape. (A scaled-only mesh has
            // zero influences, so `mgr` above is null for it.)
            if (mgr || ((proj || sc) && mesh.morphTargetManager)) mesh.morphTargetManager = null;
            // Projection reads the POST-blend positions: UVs must
            // sample the deformed geometry (project-at-rest-then-
            // stretch was measured at 3.8× stretch vs 1.01× for
            // project-after-bake). It runs BEFORE the scale fold:
            // projMatrix carries the LIVE world matrix (with the
            // fallback scaling), matching the shader, and the
            // fold below moves that scaling from node to vertices
            // without changing world positions — UVs are
            // per-vertex and unaffected.
            if (proj) projectMeshUV(mesh, proj);
            if (sc) foldScaleIntoGeometry(mesh, sc.rest, sc.factor, this.exportedSet);
            mesh.refreshBoundingInfo();
        }
    }

    restore(): void {
        for (const snapshot of this._snapshots) {
            // Guard: if the copy threw before binding, the mesh
            // still holds the original — don't dispose it.
            const working = snapshot.mesh.geometry;
            if (working !== snapshot.geometry) {
                snapshot.geometry.applyToMesh(snapshot.mesh);
                working?.dispose();
                // applyToMesh only rebuilds ONE global submesh —
                // and only when the bake's ranges no longer fit
                // the restored totals. Neither outcome is the
                // original submesh split, so a multi-material
                // mesh would render wrong ranges live; recreate
                // the captured ranges explicitly.
                snapshot.mesh.releaseSubMeshes();
                for (const r of snapshot.subMeshes) {
                    new SubMesh(r.materialIndex, r.verticesStart, r.verticesCount, r.indexStart, r.indexCount, snapshot.mesh);
                }
            }
            snapshot.mesh.morphTargetManager = snapshot.manager;
            // The scale fold moved the fallback sizing onto the
            // throwaway vertices and parked REST scaling on the
            // node — give the live mesh its solved scaling back.
            snapshot.mesh.scaling.copyFrom(snapshot.scaling);
            snapshot.mesh.computeWorldMatrix(true);
            // applyMorph: the manager is back, so the bounds must cover the
            // morphed shape again — rest-pose bounds shrink the pick area.
            snapshot.mesh.refreshBoundingInfo(false, true);
        }
    }
}

/**
 * Which of `candidates` sample a projected texture, with per-submesh ranges
 * and the matrix that reproduces the editor preview shader's mapping for
 * each. A range projects when its EFFECTIVE material (the sub-material, for
 * a MultiMaterial mesh) resolves a projection policy — asset tag, live
 * preview plugin, or textured-material heuristic; see
 * {@link resolveTriplanarForBake}. Any single projected map (albedo, normal,
 * or ORM) qualifies — each smears on its own. Ranges whose material
 * resolves nothing keep their authored UVs through the rebuild.
 *
 * Remaining gates (each warns and skips projection — morphs still bake):
 * missing normals, vertex streams the weld cannot rebuild (see
 * {@link PROJECTABLE_KINDS}), and multi-range UNINDEXED meshes (submesh
 * ranges address the index buffer; with no index buffer there is nothing to
 * slice per material).
 */
function planProjections(
    candidates: Mesh[],
    objectRoot: TransformNode,
    originalNames?: Map<Mesh, string>,
): Map<Mesh, MeshProjection> {
    const projections = new Map<Mesh, MeshProjection>();
    for (const mesh of candidates) {
        const subMeshes = mesh.subMeshes ?? [];
        if (subMeshes.length === 0) continue;
        // getMaterial(false) resolves a MultiMaterial to the range's
        // sub-material (and never conjures scene.defaultMaterial); for a
        // single-material mesh it is mesh.material.
        const recorded = originalNames?.get(mesh);
        const resolved = subMeshes.map((sm) => {
            const material = sm.getMaterial(false);
            return resolveTriplanarForBake(material, objectRoot, material?.name, recorded);
        });
        if (!resolved.some((r) => r !== null)) continue;
        if (!mesh.isVerticesDataPresent(VertexBuffer.NormalKind)) {
            Logger.warn(`[TraceExport] uvproject ${mesh.name}: no normals — UVs left as authored`);
            continue;
        }
        const extraKinds = mesh.getVerticesDataKinds().filter((k) => !PROJECTABLE_KINDS.has(k)
            && !(mesh.morphTargetManager && MORPH_TARGET_STREAM.test(k)));
        if (extraKinds.length) {
            Logger.warn(`[TraceExport] uvproject ${mesh.name}: unsupported vertex streams (${extraKinds.join(', ')}) — UVs left as authored`);
            continue;
        }
        if (subMeshes.length > 1 && !mesh.getIndices()) {
            Logger.warn(`[TraceExport] uvproject ${mesh.name}: multi-material ranges on an unindexed mesh — UVs left as authored`);
            continue;
        }
        mesh.computeWorldMatrix(true);
        const ranges = subMeshes.map((sm, i): RangeProjection => {
            const projection = resolved[i];
            if (!projection) {
                return {
                    materialIndex: sm.materialIndex,
                    indexStart: sm.indexStart,
                    indexCount: sm.indexCount,
                    config: null, projMatrix: null, normalMatrix: null,
                };
            }
            // Sampling is anchored to the anchor node's RIGID frame —
            // rotation + translation only, scale excluded — via the SAME
            // helper the preview shader's bindForSubMesh uses
            // (rigidFrameInverseToRef), so preview and bake cannot decompose
            // differently. projMatrix maps mesh-local → world → inverse
            // rigid anchor (Babylon row-vector order), mirroring the
            // shader's triInvAnchor × (world × vertex). Per range because
            // each sub-material can anchor to a different node.
            const anchor = projection.anchor && !projection.anchor.isDisposed()
                ? projection.anchor
                : mesh;
            anchor.computeWorldMatrix(true);
            const invAnchorRigid = new Matrix();
            rigidFrameInverseToRef(anchor, invAnchorRigid);
            const projMatrix = mesh.getWorldMatrix().multiply(invAnchorRigid);
            return {
                materialIndex: sm.materialIndex,
                indexStart: sm.indexStart,
                indexCount: sm.indexCount,
                config: projection.config, projMatrix,
                normalMatrix: projMatrix.clone().invert().transpose(),
            };
        });
        projections.set(mesh, { ranges });
    }
    return projections;
}

function asFloat32(data: FloatArray): Float32Array {
    return data instanceof Float32Array ? data : new Float32Array(data);
}

/** A range's slice of an index buffer, without copying typed arrays. */
function sliceIndices(indices: IndicesArray, start: number, count: number): ArrayLike<number> {
    if (start === 0 && count === indices.length) return indices;
    return ArrayBuffer.isView(indices)
        ? indices.subarray(start, start + count)
        : indices.slice(start, start + count);
}

/**
 * Re-project a baked mesh's UVs to reproduce the runtime triplanar shader's
 * mapping, writing the welded result (positions/normals/uvs/tangents/indices)
 * onto the mesh's throwaway bake geometry. Reads whatever positions the mesh
 * carries WHEN CALLED — the caller runs this after the morph blend, so the
 * UVs sample the deformed geometry. The caller's whole-geometry snapshot
 * restores the live mesh afterwards (submesh ranges included).
 *
 * Each submesh range is rebuilt separately — unindex → project (or keep
 * authored UVs) → tangents → weld — then the ranges are concatenated into
 * one vertex buffer with fresh submeshes. Per-range welding is what keeps a
 * multi-material mesh exportable: the glTF exporter writes one primitive per
 * submesh, slicing attribute accessors by [verticesStart, verticesCount) and
 * rebasing indices by -verticesStart, so every range must own a contiguous
 * vertex block. A vertex shared by two ranges is duplicated, which glTF
 * primitives require anyway.
 */
function projectMeshUV(mesh: Mesh, proj: MeshProjection): void {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    if (!positions || !normals) return; // gated in planProjections
    const pos32 = asFloat32(positions);
    const nor32 = asFloat32(normals);
    const indices = mesh.getIndices(); // null only in the single-range case (gated)
    const multi = proj.ranges.length > 1;
    const authoredUvData = mesh.getVerticesData(VertexBuffer.UVKind);
    const authoredUv = authoredUvData ? asFloat32(authoredUvData) : null;

    const blocks: { positions: Float32Array; normals: Float32Array; uvs: Float32Array; tangents: Float32Array; indices: Uint32Array }[] = [];
    const outRanges: { materialIndex: number; verticesStart: number; verticesCount: number; indexStart: number; indexCount: number }[] = [];
    let vertexBase = 0;
    let indexBase = 0;
    for (const [rangeIndex, range] of proj.ranges.entries()) {
        const rangeIndices = indices ? sliceIndices(indices, range.indexStart, range.indexCount) : null;
        // Authored UVs are only carried into the soup for ranges the
        // projection does NOT rewrite — projected ranges replace them.
        const soup = toNonIndexed(pos32, nor32, range.config ? null : authoredUv, rangeIndices);
        let uv: Float32Array;
        if (range.config) {
            // tileSize only. The material's Texture Scale (texture uScale/vScale) is
            // deliberately NOT folded in: GLTF2Export writes it onto each
            // textureInfo as KHR_texture_transform.scale when the mesh is
            // serialized, so adding it here would apply it twice. The editor's
            // projection preview multiplies the same factor into its sampling
            // (TriplanarPBRPlugin); texture-scale-export.test.ts pins both halves.
            const projected = buildProjectedUV(soup.positions, soup.normals, range.projMatrix!, range.normalMatrix!, {
                scale: range.config.tileSize,
            });
            uv = projected.uv;
            // The fidelity metric: 1.0 = the texture tiles at exactly the requested density.
            Logger.log(`[TraceExport] uvproject ${mesh.name}${multi ? `#${rangeIndex}` : ''}: stretch avg ${projected.stretchStats.avg.toFixed(3)} max ${projected.stretchStats.max.toFixed(3)}`);
        } else {
            // Unprojected range of a multi-material mesh: authored UVs ride
            // through (zeros when the mesh never had any — the rebuilt UV
            // buffer must still cover every range).
            uv = soup.uvs ?? new Float32Array((soup.positions.length / 3) * 2);
        }
        // Tangents for every range, projected or not: the rebuilt mesh has
        // ONE tangent buffer, and the exporter emits TANGENT on each
        // primitive that shares it.
        const tangents = computeTangents(soup.positions, soup.normals, uv);
        const welded = weldByAttributes({ positions: soup.positions, normals: soup.normals, uvs: uv, tangents });
        const verticesCount = welded.positions.length / 3;
        blocks.push({
            positions: welded.positions,
            normals: welded.normals,
            uvs: welded.uvs,
            tangents: welded.tangents!,
            indices: welded.indices,
        });
        outRanges.push({
            materialIndex: range.materialIndex,
            verticesStart: vertexBase,
            verticesCount,
            indexStart: indexBase,
            indexCount: welded.indices.length,
        });
        vertexBase += verticesCount;
        indexBase += welded.indices.length;
    }

    // Concatenate the per-range blocks; indices rebase onto each block's
    // verticesStart.
    const allPositions = new Float32Array(vertexBase * 3);
    const allNormals = new Float32Array(vertexBase * 3);
    const allUvs = new Float32Array(vertexBase * 2);
    const allTangents = new Float32Array(vertexBase * 4);
    const allIndices = new Uint32Array(indexBase);
    for (let b = 0; b < blocks.length; b++) {
        const block = blocks[b];
        const out = outRanges[b];
        allPositions.set(block.positions, out.verticesStart * 3);
        allNormals.set(block.normals, out.verticesStart * 3);
        allUvs.set(block.uvs, out.verticesStart * 2);
        allTangents.set(block.tangents, out.verticesStart * 4);
        for (let i = 0; i < block.indices.length; i++) {
            allIndices[out.indexStart + i] = block.indices[i] + out.verticesStart;
        }
    }

    // Positions first — they set the geometry's vertex count for the rest.
    mesh.setVerticesData(VertexBuffer.PositionKind, allPositions, true);
    mesh.setVerticesData(VertexBuffer.NormalKind, allNormals, true);
    mesh.setVerticesData(VertexBuffer.UVKind, allUvs, true);
    mesh.setVerticesData(VertexBuffer.TangentKind, allTangents, true);
    mesh.setIndices(allIndices, vertexBase);
    if (multi) {
        // setIndices force-collapsed the submeshes to one global range —
        // carve the rebuilt material ranges back out so the exporter writes
        // one primitive per original sub-material.
        mesh.releaseSubMeshes();
        for (const r of outRanges) {
            new SubMesh(r.materialIndex, r.verticesStart, r.verticesCount, r.indexStart, r.indexCount, mesh);
        }
    }
}

/**
 * Fold a scale-fallback sizing into the baked vertex data and park the REST
 * scaling on the mesh node for serialization, so the GLB carries unit-ish
 * transforms with target-size geometry. World appearance is unchanged:
 * (P × factor) under rest scaling ≡ P under the solved scaling. With the
 * MorphStretch data kept in the scene JSON, this is what holds the no-op
 * invariant on reload — rest bounds measure AT the target size, so the
 * contain/fallback factor resolves to ≈ 1 and nothing double-scales.
 *
 * Normals transform by the INVERSE scale (then renormalize); tangents lie in
 * the surface, so they transform by the DIRECT scale (renormalized,
 * handedness kept). The caller's snapshot restores the live node's scaling.
 */
function foldScaleIntoGeometry(mesh: Mesh, rest: Vector3, factor: Vector3, exported?: ReadonlySet<Node>): void {
    // Refuse the fold when this mesh has children that also ship.
    //
    // The fold moves the sizing from the node into the mesh's own vertices and
    // parks REST scaling on the node — world-identical for the mesh itself,
    // and wrong for anything parented under it: a child at local offset `t`
    // would serialize at `rest × t` where the live scene shows `solved × t`.
    // Compensating the children is not the safe floor either, because the live
    // view is already anomalous on such chains (each owned mesh takes the
    // factor individually, so a nested pair renders at f² — see the disclaimer
    // in morphstretch.ts). Skipping keeps the asset's own geometry honest and
    // says so, rather than writing displaced children with confidence.
    const shippingChildren = exported
        ? mesh.getChildren().filter((child) => exported.has(child)).length
        : 0;
    if (shippingChildren > 0) {
        Logger.warn(`[TraceExport] scalebake ${mesh.name}: has ${shippingChildren} exported child node(s) — skipping the scale fold, so this mesh ships at its rest size`);
        return;
    }
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions) return;
    const folded = new Float32Array(positions.length);
    for (let i = 0; i < positions.length; i += 3) {
        folded[i] = positions[i] * factor.x;
        folded[i + 1] = positions[i + 1] * factor.y;
        folded[i + 2] = positions[i + 2] * factor.z;
    }
    mesh.setVerticesData(VertexBuffer.PositionKind, folded, true);
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    if (normals) {
        const out = new Float32Array(normals.length);
        for (let i = 0; i < normals.length; i += 3) {
            const x = normals[i] / factor.x;
            const y = normals[i + 1] / factor.y;
            const z = normals[i + 2] / factor.z;
            const len = Math.hypot(x, y, z) || 1;
            out[i] = x / len;
            out[i + 1] = y / len;
            out[i + 2] = z / len;
        }
        mesh.setVerticesData(VertexBuffer.NormalKind, out, true);
    }
    const tangents = mesh.getVerticesData(VertexBuffer.TangentKind);
    if (tangents) {
        const out = new Float32Array(tangents.length);
        for (let i = 0; i < tangents.length; i += 4) {
            const x = tangents[i] * factor.x;
            const y = tangents[i + 1] * factor.y;
            const z = tangents[i + 2] * factor.z;
            const len = Math.hypot(x, y, z) || 1;
            out[i] = x / len;
            out[i + 1] = y / len;
            out[i + 2] = z / len;
            out[i + 3] = tangents[i + 3];
        }
        mesh.setVerticesData(VertexBuffer.TangentKind, out, true);
    }
    mesh.scaling.copyFrom(rest);
    mesh.computeWorldMatrix(true);
}

/**
 * Apply the editor → Unity AR Foundation image-anchor frame conversion
 * to direct children of the scene root in an export hierarchy, in place.
 * Self-inverse — same function as the import path uses
 * (see {@link zipAnchorConversion} / `applyImageAnchorFrameConversion`
 * in `loadCmsScene`).
 *
 * Exported for test access — call sites should go through `TraceExport`.
 */
export function applyEditorToUnityZipConversion(
    hierarchy: SceneObjectData[],
): void {
    for (const node of hierarchy) {
        const t = node.transform;
        // `t.parent` is the serialized parent name (a string), not a
        // Babylon transform reference. Direct children of the scene root
        // have it unset; anything with a parent name is nested inside
        // another node and its transform is already relative — no frame
        // conversion needed.
        if (!t || t.parent) continue;
        if (t.translation) {
            t.translation = convertImageAnchorPosition(t.translation) ?? t.translation;
        }
        if (t.rotation) {
            t.rotation = convertImageAnchorRotation(t.rotation) ?? t.rotation;
        }
    }
}

